/*
 * Charm builder for Dawn.
 * Plain JavaScript, no dependencies. Works with the charm-builder and
 * charm-builder-data sections.
 */
(() => {
  if (customElements.get('charm-builder')) return;

  const MAX_PAGES = 40; // 40 pages x 50 products = 2,000 products per collection

  /* ---------- helpers ---------- */

  const esc = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);

  // "Silver", "SIlver", "Stainless steel" -> silver; "Gold", "Rose gold" -> gold.
  const classifyMetal = (value) => {
    const v = String(value || '').toLowerCase();
    if (!v) return null;
    if (v.includes('gold')) return 'gold';
    if (/s[il]{1,2}ver/.test(v) || v.includes('steel')) return 'silver';
    return null;
  };

  const metalFromList = (list) => {
    const found = new Set((list || []).map(classifyMetal).filter(Boolean));
    return found.size === 1 ? [...found][0] : null;
  };

  // Option values like "Small", "Med", "L" -> small / medium / large.
  const SIZES = {
    small: /^(small|sm|s)$|\bsmall\b/,
    medium: /^(medium|med|md|m)$|\bmedium\b/,
    large: /^(large|lg|l)$|\blarge\b/,
  };
  const classifySize = (value) => {
    const v = String(value || '').trim().toLowerCase();
    return Object.keys(SIZES).find((size) => SIZES[size].test(v)) || null;
  };

  const metalLabel = (metal) => ({ silver: 'Silver', gold: 'Gold', both: 'Silver or gold' })[metal] || '';

  const countLabel = (count, noun) => `${count} ${count === 1 ? noun : `${noun}s`}`;

  // A design position holds null (an empty bracelet link), a charm variant key, or a plain link
  // token in either finish. Only these two exact tokens are plain links; anything else is a charm key.
  const PLAIN_METALS = ['silver', 'gold'];
  // 'plain:silver' follows the bracelet's finish (or keychain hardware) when it changes; 'plain:gold:accent'
  // is a finish the shopper chose against it and stays as placed.
  const plainToken = (metal, accent = false) => `plain:${metal}${accent ? ':accent' : ''}`;
  const plainMetalOf = (key) => (typeof key === 'string' && /^plain:(silver|gold)(:accent)?$/.exec(key)?.[1]) || null;
  const otherMetal = (metal) => (metal === 'gold' ? 'silver' : 'gold');

  // "Nurse's Cap Italian Charm" -> "Nurse's Cap"
  const cleanName = (title) => String(title).replace(/\s*[-|]?\s*italian\s+charms?\s*$/i, '').trim() || String(title);

  const sizedImage = (url, width) => (url ? url.replace(/([?&])width=\d+/, `$1width=${width}`) : url);

  // "16 | 5.7 in" per line -> [{ links: 16, label: '5.7 in' }]
  const parseSizes = (text) =>
    String(text || '')
      .split(/\r?\n/)
      .map((line) => {
        const [num, ...rest] = line.split('|');
        const links = parseInt(num, 10);
        if (!(links > 0 && links <= 40)) return null;
        return { links, label: rest.join('|').trim() };
      })
      .filter(Boolean)
      .filter((c, i, all) => all.findIndex((o) => o.links === c.links) === i)
      .sort((a, b) => a.links - b.links);

  const sizeText = (choice) => (choice.label ? `${choice.label} (${choice.links} links)` : `${choice.links} links`);

  function flatten(products, defaultMetal) {
    const items = [];
    for (const product of products || []) {
      const options = product.options || [];
      const metalOption = options.findIndex((name) => /colou?r|metal|finish|tone/i.test(name));
      const tagMetal = metalFromList(product.tags);
      const titleMetal = classifyMetal(product.title);
      const hasVariants = product.variants.length > 1;

      for (const variant of product.variants) {
        const optionMetal = metalOption >= 0 ? classifyMetal(variant.options[metalOption]) : null;
        const metal =
          optionMetal || (hasVariants ? classifyMetal(variant.title) : null) || tagMetal || titleMetal || defaultMetal || null;
        const variantLabel = variant.title && variant.title !== 'Default Title' ? variant.title : '';
        const optionValues = (variant.options || []).filter((v) => v !== 'Default Title');
        const size =
          (variant.options || []).map((v, i) => (i === metalOption ? null : classifySize(v))).find(Boolean) ||
          classifySize(product.title);
        items.push({
          key: String(variant.id),
          id: variant.id,
          productId: product.id,
          size,
          // "SIlver / Small" -> "Silver / Small"
          optionLabel: optionValues.map((v) => metalLabel(classifyMetal(v)) || v).join(' / '),
          name: [cleanName(product.title), ...optionValues.filter((v, i) => i !== metalOption && !classifyMetal(v))].filter((v, i, a) => a.indexOf(v) === i).join(' · '),
          title: product.title,
          variantLabel,
          metal,
          price: Number(variant.price) || 0,
          available: Boolean(variant.available) && !(variant.tracked && variant.quantity <= 0) && variant.max !== 0,
          max: variant.tracked ? Math.max(0, Number(variant.quantity) || 0) : typeof variant.max === 'number' ? variant.max : null,
          image: variant.image || product.image || null,
          collections: product.collections || [],
          collectionTitles: product.collectionTitles || [],
          haystack: [product.title, variantLabel, ...(product.tags || [])].join(' ').toLowerCase(),
        });
      }
    }
    return items;
  }

  // Theme buttons: every collection holding a charm, named by its Shopify title and sorted by name. A theme
  // block renames one or puts it first. The charm, plain-link and keychain collections and any collection
  // picked in "Collections not shown as theme buttons" never get a button.
  function deriveCategories(charms, config, types) {
    const hidden = new Set([config.charmCollection, ...types.map((t) => t.collection), ...(config.hiddenCategories || [])]);
    const pinned = (config.categories || []).filter((c, i, all) => c.handle && !hidden.has(c.handle) && all.findIndex((o) => o.handle === c.handle) === i);
    const found = new Map();
    for (const charm of charms) {
      charm.collections.forEach((handle, i) => {
        if (!found.has(handle)) found.set(handle, charm.collectionTitles[i] || handle);
      });
    }
    const automatic = [...found]
      .filter(([handle]) => !hidden.has(handle) && !pinned.some((c) => c.handle === handle))
      .map(([handle, label]) => ({ handle, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    return [...pinned, ...automatic];
  }

  /* ---------- custom element ---------- */

  class CharmBuilder extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;

      const configEl = this.querySelector('[data-cb-config]');
      if (!configEl) return;
      this.config = JSON.parse(configEl.textContent);

      const q = (sel) => this.querySelector(sel);
      this.els = {
        panel: q('[data-cb-panel]'),
        types: q('[data-cb-types]'),
        bases: q('[data-cb-bases]'),
        sizes: q('[data-cb-sizes]'),
        preview: q('[data-cb-preview]'),
        detail: q('[data-cb-detail]'),
        summary: q('[data-cb-summary]'),
        add: q('[data-cb-add]'),
        hint: q('[data-cb-hint]'),
        reset: q('[data-cb-reset]'),
        error: q('[data-cb-error]'),
        live: q('[data-cb-live]'),
        count: q('[data-cb-count]'),
        metal: q('[data-cb-metal]'),
        search: q('[data-cb-search]'),
        categories: q('[data-cb-categories]'),
        grid: q('[data-cb-grid]'),
        bar: q('[data-cb-bar]'),
        barStatus: q('[data-cb-bar-status]'),
        barTotal: q('[data-cb-bar-total]'),
        barAdd: q('[data-cb-bar-add]'),
        feedback: q('[data-cb-feedback]'),
        saveStatus: q('[data-cb-save-status]'),
        undo: q('[data-cb-undo]'),
        redo: q('[data-cb-redo]'),
        options: q('[data-cb-options]'),
        browsePicks: q('[data-cb-browse-picks]'),
        collapse: q('[data-cb-collapse]'),
        barMore: q('[data-cb-bar-more]'),
        checkout: q('[data-cb-checkout]'),
        layout: q('.cb__layout'),
        sheet: q('[data-cb-sheet]'),
        sheetBody: q('[data-cb-sheet-body]'),
        tools: q('.cb__workspace-tools'),
        history: q('.cb__history'),
      };
      if (!this.els.grid) return;

      let root = this.config.root || '/';
      if (!root.endsWith('/')) root += '/';
      this.root = root;

      try {
        this.money = new Intl.NumberFormat(this.config.locale || undefined, {
          style: 'currency',
          currency: this.config.currency || 'USD',
        });
      } catch (e) {
        this.money = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' });
      }

      this.types = (this.config.types || []).filter((t) => t.collection);
      this.categories = [];
      this.filters = { metal: 'all', category: 'all', query: '' };
      this.builds = {};
      this.types.forEach((t) => {
        t.choices = parseSizes(t.sizeList);
        if (!t.measurementsConfirmed) t.choices = t.choices.map((c) => ({ ...c, label: '' }));
        const start = t.choices.find((c) => c.links === t.slots) || t.choices[0];
        const length = start ? start.links : null;
        this.builds[t.id] = { baseKey: null, metal: null, length, slots: new Array(length || t.slots).fill(null) };
      });
      this.activeType = this.types[0] ? this.types[0].id : null;
      this.selected = null;
      this.view = 'ring';
      this.history = [];
      this.future = [];

      this.cartCounts = new Map();
      this.pendingKey = `${this.config.storageKey}:pending`;
      try { this.pending = JSON.parse(sessionStorage.getItem(this.pendingKey) || 'null'); } catch { this.pending = null; }
      this.onResume = () => { if (document.visibilityState === 'visible' && this.charms && Date.now() - this.checkedAt > 60000 && !this.busy) this.refreshStock(); };
      document.addEventListener('visibilitychange', this.onResume);
      this.statusDelay = 4000;
      this.watchModes();
      this.bindEvents();
      this.initCoach();
      this.load();
    }

    // A one-time tip on touch phones (shown by CSS only there) until dismissed or a drag succeeds.
    initCoach() {
      let seen = false;
      try { seen = localStorage.getItem(`${this.config.storageKey}:tip`) === 'seen'; } catch { /* Show it this visit. */ }
      this.toggleAttribute('data-coach', !seen);
      this.querySelector('[data-cb-coach-close]')?.addEventListener('click', () => this.dismissCoach());
    }

    dismissCoach() {
      if (!this.hasAttribute('data-coach')) return;
      this.removeAttribute('data-coach');
      try { localStorage.setItem(`${this.config.storageKey}:tip`, 'seen'); } catch { /* Hidden for this visit. */ }
    }

    // Two independent switches. data-app: phone layout (pinned design, pickers, image grid, bottom bar).
    // data-touch: touch input, which never starts a native drag (it fights scrolling and opens image menus).
    watchModes() {
      const query = (q) => (typeof window.matchMedia === 'function' ? window.matchMedia(q) : null);
      // The pinned-design layout with pickers is used at every size (owner request, October 9: desktop
      // gets the same clean flow as phones); CSS adapts it to phones, phones on their side and desktop.
      this.appQuery = query('all');
      this.touchQuery = query('(hover: none) and (pointer: coarse)');
      this.onModeChange = () => {
        const app = !!this.appQuery?.matches;
        const touch = !!this.touchQuery?.matches;
        const changed = app !== this.app || touch !== this.touch;
        this.app = app;
        this.touch = touch;
        this.toggleAttribute('data-app', app);
        this.toggleAttribute('data-touch', touch);
        // Phones: Undo and Redo follow the last picker (Finish on keychains, Size on bracelets) so the
        // design below uses the full width; desktop keeps them with the view switch.
        const home = app ? this.els.options : this.els.tools;
        if (this.els.history && home && this.els.history.parentElement !== home) home.append(this.els.history);
        if (!app) { this.setOrderOpen(false); this.closeSheet(); }
        if (changed && this.charms) this.renderAll();
        this.updateBar?.();
      };
      [this.appQuery, this.touchQuery].forEach((q) => q?.addEventListener?.('change', this.onModeChange));
      this.onModeChange();
    }

    // Focus without moving the page on phones and touch screens; desktop keyboard users keep the scroll.
    focusQuietly(el) {
      el?.focus?.({ preventScroll: !!(this.app || this.touch) });
    }

    disconnectedCallback() {
      this.cancelTouchDrag?.();
      this.dragOffController?.abort();
      this.stopDragScroll?.();
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('visibilitychange', this.onResume);
      this.panelObserver?.disconnect();
      window.removeEventListener('resize', this.positionPanel);
      if (this.refit) window.removeEventListener('resize', this.refit);
      [this.appQuery, this.touchQuery].forEach((q) => q?.removeEventListener?.('change', this.onModeChange));
      document.removeEventListener('pointerdown', this.onOutsideOrder);
      clearTimeout(this.statusTimer);
    }

    /* ----- state accessors ----- */

    get type() {
      return this.types.find((t) => t.id === this.activeType);
    }

    get build() {
      return this.builds[this.activeType];
    }

    formatMoney(cents) {
      return this.money.format((cents || 0) / 100);
    }

    countOf(key, ignoreIndex = -1) {
      return this.build.slots.reduce((n, k, i) => n + (k === key && i !== ignoreIndex ? 1 : 0), 0);
    }

    // Plain links in one finish, accents included.
    plainCount(metal, ignoreIndex = -1) {
      return this.build.slots.reduce((n, k, i) => n + (plainMetalOf(k) === metal && i !== ignoreIndex ? 1 : 0), 0);
    }

    // A plain link in the current finish follows later finish changes; the other finish is an accent.
    plainKey(metal) {
      return plainToken(metal, metal !== this.baseMetal());
    }

    // Links on a base with a Small / Medium / Large size (keychains), else 0.
    baseLinks(type, base) {
      const sized = base && base.size && type.sizes ? Number(type.sizes[base.size]) : 0;
      return sized > 0 ? sized : 0;
    }

    // Link count for a design: the chosen bracelet size, else the base's size, else the default.
    slotsFor(type, build) {
      if (type.flexible) return build.slots.length;
      if (type.choices.length && build.length) return build.length;
      return this.baseLinks(type, type.baseMap && type.baseMap.get(build.baseKey)) || type.slots;
    }

    minCharms(type) { return type.flexible ? 1 : 0; }

    // Grow or shrink a design to fit its base. Charms and plain links keep their order; when a
    // smaller base can't hold them all, gaps close first, then extras come off the end.
    // Returns the number of chosen links (charms or plain links) taken off.
    fitToBase(type, build) {
      const count = this.slotsFor(type, build);
      const slots = build.slots;
      if (slots.length === count) return 0;
      if (slots.length < count) {
        build.slots = slots.concat(new Array(count - slots.length).fill(null));
        return 0;
      }
      if (slots.slice(count).every((k) => !k)) {
        build.slots = slots.slice(0, count);
        return 0;
      }
      const filled = slots.filter(Boolean);
      const compact = filled.slice(0, count);
      build.slots = compact.concat(new Array(count - compact.length).fill(null));
      return Math.max(0, filled.length - count);
    }

    baseMetal(type = this.type, build = this.build) {
      const base = type?.baseMap?.get(build?.baseKey);
      return base?.metal || build?.metal || 'silver';
    }

    // Plain links in either finish come from the bracelet's configured plain-link variants, whatever
    // the design's own finish; keychains use the same variants for their plain spacer links.
    plainVariant(metal) {
      const source = this.types.find((t) => !t.flexible);
      return source?.bases?.find((b) => b.metal === metal) || null;
    }

    // Why a plain link in this finish can't be placed (ignoring the link it would replace), or ''.
    plainProblem(metal, ignoreIndex = -1) {
      const item = this.plainVariant(metal);
      if (!item || !item.available) return `Plain ${metal} links are not available right now.`;
      if (this.plainCount(metal, ignoreIndex) >= this.remaining(item)) return `No more plain ${metal} links are available.`;
      return '';
    }

    /* ----- loading ----- */

    async fetchPage(handle, page) {
      const url = `${this.root}collections/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(
        this.config.dataSection
      )}&page=${page}`;
      const res = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!res.ok) throw new Error(`Collection "${handle}" returned ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const script = doc.querySelector('[data-cb-page]');
      if (!script) throw new Error(`Collection "${handle}" did not return builder data`);
      return JSON.parse(script.textContent);
    }

    async fetchCollection(handle) {
      const first = await this.fetchPage(handle, 1);
      if (first.pages > MAX_PAGES) throw new Error('This collection exceeds the supported catalog size.');
      const rest = [];
      for (let p = 2; p <= Math.min(first.pages || 1, MAX_PAGES); p++) rest.push(this.fetchPage(handle, p));
      const pages = await Promise.all(rest);
      return [first, ...pages].flatMap((p) => p.products || []);
    }

    async load() {
      if (!this.types.length) {
        this.showError('Choose a base bracelets or base keychains collection in the theme editor to turn on the builder.');
        this.els.grid.innerHTML = '';
        return;
      }
      try {
        await this.readCatalog();
        this.types.forEach((t) => {
          const first = t.bases.find((b) => b.available);
          if (first) { this.builds[t.id].baseKey = first.key; this.builds[t.id].metal = first.metal; }
        });
      } catch (err) {
        console.error('[charm-builder]', err);
        this.els.grid.removeAttribute('aria-busy');
        this.els.grid.innerHTML = '';
        this.showError('Charms didn’t load. Refresh the page to try again.');
        return;
      }

      this.restore();
      this.lastState = this.snapshot();
      this.renderCategories();
      this.buildGrid();
      this.applyFilters();
      this.renderAll();
      if (this.build.slots.some(Boolean)) this.announce('Your saved design is ready to keep creating.');
      this.watchPanel();
    }

    async readCatalog() {
      const [products, ...bases] = await Promise.all([
        this.fetchCollection(this.config.charmCollection),
        ...this.types.map((t) => this.fetchCollection(t.collection)),
      ]);
      // A plain-link listing added to the charms collection must not appear twice.
      const plainIds = new Set(this.types.flatMap((t) => Object.values(t.variantIds || {}).map(String)));
      const charms = flatten(products, this.config.defaultMetal).filter((c) => !plainIds.has(c.key));
      const mapped = this.types.map((t, i) => {
        const all = flatten(bases[i], null);
        if (t.flexible) return all;
        // Match plain links by their configured IDs; the setting, not the title, says which metal each is.
        return Object.entries(t.variantIds || {}).flatMap(([metal, id]) => {
          const variant = all.find((v) => v.key === String(id));
          return variant ? [{ ...variant, metal }] : [];
        });
      });
      this.charms = charms;
      this.charmMap = new Map(charms.map((c) => [c.key, c]));
      this.categories = deriveCategories(charms, this.config, this.types);
      this.types.forEach((t, i) => { t.bases = mapped[i]; t.baseMap = new Map(t.bases.map((b) => [b.key, b])); });
      this.checkedAt = Date.now();
    }

    async readCart() {
      const response = await fetch(`${this.root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Could not check your cart. Please try again.');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('Could not read your cart. Please try again.');
      this.cartCounts = new Map();
      cart.items.forEach((item) => this.cartCounts.set(String(item.variant_id || item.id), (this.cartCounts.get(String(item.variant_id || item.id)) || 0) + item.quantity));
      return cart;
    }

    remaining(item) { return item.max == null ? Infinity : Math.max(0, item.max - (this.cartCounts.get(item.key) || 0)); }

    async refreshStock() {
      this.busy = true;
      this.renderSummary();
      try {
        await this.readCatalog();
        await this.readCart();
        this.showError('');
        this.buildGrid(); this.renderCategories(); this.applyFilters(); this.renderAll();
      } catch {
        this.showError('Availability could not be refreshed. Your design is saved; try again before adding it.');
      } finally { this.busy = false; this.renderSummary(); }
    }

    /* ----- saving the design on this device ----- */

    save() {
      try {
        // Version 3: plain links are 'plain:silver' / 'plain:gold' positions (version 2 used plainSlots).
        const data = { version: 3, activeType: this.activeType, builds: this.builds };
        localStorage.setItem(this.config.storageKey, JSON.stringify(data));
        this.els.saveStatus.textContent = 'Saved on this device';
      } catch (e) {
        this.els.saveStatus.textContent = 'Not saved on this device';
      }
    }

    restore() {
      let data = null;
      try {
        data = JSON.parse(localStorage.getItem(this.config.storageKey) || 'null');
      } catch (e) {
        data = null;
      }
      if (!data || !data.builds) return;
      this.types.forEach((t) => {
        const saved = data.builds[t.id];
        if (!saved) return;
        const build = this.builds[t.id];
        const base = t.baseMap.get(String(saved.baseKey)) || t.bases.find((b) => b.metal === saved.metal);
        if (base) { build.baseKey = base.key; build.metal = base.metal; }
        else { build.baseKey = null; build.metal = saved.metal || null; }
        if (t.choices.some((c) => c.links === saved.length)) build.length = saved.length;
        const savedSlots = Array.isArray(saved.slots) ? saved.slots : [];
        if (t.flexible) build.slots = new Array(savedSlots.length).fill(null);
        this.fitToBase(t, build);
        // Version 2 saves kept deliberate plain links as empty positions listed in plainSlots, all in
        // the bracelet's finish; they become plain links in that finish.
        const legacyPlain = new Set(!t.flexible && Array.isArray(saved.plainSlots) ? saved.plainSlots : []);
        const legacyMetal = PLAIN_METALS.includes(saved.metal) ? saved.metal : this.baseMetal(t, build);
        build.slots = build.slots.map((_, i) => {
          const key = savedSlots[i];
          // Keep unavailable identities at their original positions for explicit review.
          if (typeof key === 'string' || typeof key === 'number') return String(key);
          return legacyPlain.has(i) ? plainToken(legacyMetal) : null;
        });
        // A plain link saved in the other finish is an accent.
        const savedBase = this.baseMetal(t, build);
        build.slots = build.slots.map((key) => (key === plainToken(otherMetal(savedBase)) ? plainToken(otherMetal(savedBase), true) : key));
        if (t.flexible) build.slots = build.slots.filter(Boolean);
      });
      if (this.types.some((t) => t.id === data.activeType)) this.activeType = data.activeType;
    }

    /* ----- events ----- */

    bindEvents() {
      const { metal, search, categories, grid, preview, detail, types, bases, add, barAdd, reset } = this.els;
      // Keep the submitted design stable while Shopify processes its cart request.
      this.addEventListener('click', (event) => {
        if (this.busy) { event.preventDefault(); event.stopImmediatePropagation(); }
      }, true);
      this.els.undo.addEventListener('click', () => this.travelHistory(false));
      this.els.redo.addEventListener('click', () => this.travelHistory(true));
      this.querySelectorAll('[data-cb-view]').forEach((button) => button.addEventListener('click', () => {
        this.view = button.dataset.cbView;
        this.querySelectorAll('[data-cb-view]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
        this.renderPreview();
      }));
      this.querySelector('[data-cb-review]').addEventListener('click', () => {
        this.querySelector('[data-cb-panel]').scrollIntoView({ behavior: 'auto', block: 'start' });
        this.els.preview.querySelector('[data-slot]')?.focus({ preventScroll: true });
      });

      // Phone pickers act through the same buttons as the desktop controls, so every rule (resize
      // confirmation, finish changes, filters) stays in one place. A canceled change snaps back.
      const pickTargets = {
        type: () => this.els.types.querySelectorAll('[data-type]'),
        base: () => this.els.bases.querySelectorAll('[data-base]'),
        size: () => this.els.sizes.querySelectorAll('[data-size]'),
        category: () => this.els.categories.querySelectorAll('[data-category]'),
        metal: () => this.els.metal.querySelectorAll('[data-metal]'),
      };
      const onPick = (e) => {
        const select = e.target.closest('select[data-cb-pick]');
        if (!select) return;
        const name = select.dataset.cbPick;
        const key = { type: 'type', base: 'base', size: 'size', category: 'category', metal: 'metal' }[name];
        [...pickTargets[name]()].find((b) => b.dataset[key] === select.value)?.click();
        this.renderOptions();
        this.renderBrowsePicks();
      };
      this.els.options.addEventListener('change', onPick);
      this.els.browsePicks.addEventListener('change', onPick);
      this.els.browsePicks.addEventListener('click', (e) => {
        if (!e.target.closest('[data-cb-search-toggle]')) return;
        const open = !this.hasAttribute('data-searching');
        this.toggleAttribute('data-searching', open);
        this.renderBrowsePicks();
        if (open) this.focusQuietly(this.els.search);
      });
      this.els.collapse.addEventListener('click', () => {
        const collapsed = !this.hasAttribute('data-collapsed');
        this.toggleAttribute('data-collapsed', collapsed);
        this.els.collapse.setAttribute('aria-expanded', String(!collapsed));
        this.querySelector('[data-cb-collapse-label]').textContent = collapsed ? 'Show your design' : 'Hide your design';
      });
      this.els.barMore.addEventListener('click', () => this.setOrderOpen(!this.hasAttribute('data-order-open')));
      // The price sheet closes on Escape or a tap outside it and the bar.
      this.onOutsideOrder = (e) => {
        if (!this.hasAttribute('data-order-open') || this.els.checkout.contains(e.target) || this.els.bar.contains(e.target)) return;
        this.setOrderOpen(false);
      };
      document.addEventListener('pointerdown', this.onOutsideOrder);

      metal.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-metal]');
        if (!btn) return;
        this.filters.metal = btn.dataset.metal;
        metal.querySelectorAll('[data-metal]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        this.applyFilters();
        this.updateBadges();
      });

      let searchTimer;
      search.addEventListener('input', () => {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => {
          this.filters.query = search.value.trim().toLowerCase();
          this.applyFilters();
        }, 120);
      });

      categories.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-category]');
        if (!btn) return;
        this.filters.category = btn.dataset.category;
        categories.querySelectorAll('[data-category]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        this.applyFilters();
      });

      // Charm tiles: click or Enter/Space places the charm; on phones it opens the charm's details first.
      const chooseCharm = (tile) => (this.app ? this.openSheet(tile.dataset.key, tile) : this.place(tile.dataset.key));
      grid.addEventListener('click', (e) => {
        const plainTile = e.target.closest('[data-plain-link]');
        if (plainTile) return this.app ? this.openSheet(plainToken(plainTile.dataset.plainMetal), plainTile) : this.placePlain(undefined, plainTile.dataset.plainMetal);
        const tile = e.target.closest('[data-key]');
        if (tile) chooseCharm(tile);
        if (e.target.closest('[data-cb-clear-filters]')) this.clearFilters();
      });
      grid.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const tile = e.target.closest('[data-key]');
        if (!tile) return;
        e.preventDefault();
        chooseCharm(tile);
      });

      const sheet = this.els.sheet;
      sheet?.addEventListener('click', (e) => {
        // A tap on the dimmed backdrop lands on the dialog itself.
        if (e.target === sheet || e.target.closest('[data-cb-sheet-close]')) return this.closeSheet();
        if (!e.target.closest('[data-cb-sheet-add]') || this.sheetKey == null) return;
        const plain = plainMetalOf(this.sheetKey);
        if (plain ? this.placePlain(undefined, plain) : this.place(this.sheetKey)) this.closeSheet(); else this.renderSheet();
      });
      sheet?.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        e.stopPropagation();
        this.closeSheet();
      });
      sheet?.addEventListener('close', () => this.sheetClosed());

      // Links on the preview: click selects, Enter/Space selects.
      preview.addEventListener('click', (e) => {
        if (e.target.closest('[data-cb-append]')) { this.selected = null; this.focusQuietly(this.els.grid.querySelector('[data-key]:not([hidden])')); return; }
        const link = e.target.closest('[data-slot]');
        if (link) this.select(Number(link.dataset.slot));
      });
      preview.addEventListener('keydown', (e) => {
        const link = e.target.closest('[data-slot]');
        if (!link) return;
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
          e.preventDefault();
          const current = Number(link.dataset.slot);
          const next = e.key === 'Home' ? 0 : e.key === 'End' ? this.build.slots.length - 1 : current + (e.key === 'ArrowLeft' ? -1 : 1);
          this.focusSlot(Math.max(0, Math.min(this.build.slots.length - 1, next)));
        } else if (e.key === 'Escape') {
          e.preventDefault();
          this.plainMode = false;
          this.selected = null;
          this.renderPreview(); this.renderDetail(); this.updateBadges();
          this.els.feedback.hidden = true;
          this.focusSlot(Number(link.dataset.slot));
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.select(Number(link.dataset.slot));
        } else if ((e.key === 'Delete' || e.key === 'Backspace') && this.build.slots[Number(link.dataset.slot)]) {
          e.preventDefault();
          this.removeAt(Number(link.dataset.slot));
        }
      });

      // Escape anywhere in the builder cancels a selection or the plain-link picker, or closes the price sheet.
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !e.defaultPrevented && this.hasAttribute('data-order-open')) {
          e.preventDefault();
          this.setOrderOpen(false);
          this.focusQuietly(this.els.barMore);
          return;
        }
        if (e.key !== 'Escape' || e.defaultPrevented || (this.selected == null && !this.plainMode)) return;
        const index = this.selected;
        const fromPlain = this.plainMode;
        this.plainMode = false;
        this.selected = null;
        this.renderPreview(); this.renderDetail(); this.updateBadges();
        this.els.feedback.hidden = true;
        if (index != null) this.focusSlot(index); else if (fromPlain) this.plainTiles?.[this.plainMetal]?.focus();
      });

      detail.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;
        const i = this.selected;
        if (btn.dataset.action === 'done') this.select(null);
        if (btn.dataset.action === 'remove') this.removeAt(i);
        if (btn.dataset.action === 'plain') this.placePlain(i, btn.dataset.metal);
        if (btn.dataset.action === 'move') this.move(i, Number(btn.dataset.to));
      });

      types.addEventListener('click', (e) => {
        const tab = e.target.closest('[data-type]');
        if (!tab || tab.dataset.type === this.activeType) return;
        this.plainMode = false;
        this.activeType = tab.dataset.type;
        this.selected = null;
        this.renderAll();
        this.afterChange();
      });

      bases.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-base]');
        if (!btn || btn.disabled || btn.dataset.base === this.build.baseKey) return;
        const type = this.type;
        const before = this.build.slots.length;
        const previous = this.build.baseKey ? this.baseMetal() : null;
        this.build.baseKey = btn.dataset.base;
        this.build.metal = type.baseMap.get(btn.dataset.base)?.metal;
        const metal = this.baseMetal();
        // Plain links that follow the finish change with it; accents the shopper chose stay as placed.
        let converted = 0;
        if (previous && previous !== metal && this.plainVariant(metal)) {
          this.build.slots = this.build.slots.map((key) => {
            if (key !== plainToken(previous)) return key;
            converted += 1;
            return plainToken(metal);
          });
        }
        const removed = this.fitToBase(type, this.build);
        const count = this.build.slots.length;
        if (this.selected != null && this.selected >= count) this.selected = null;
        this.renderBases();
        this.afterChange(
          removed
            ? `This ${type.label.toLowerCase()} has ${count} links, so the last ${removed} ${removed === 1 ? 'link was' : 'links were'} taken off.`
            : count !== before
            ? `This ${type.label.toLowerCase()} has ${count} links.`
            : converted
            ? `${metalLabel(metal)} finish selected. ${converted} plain ${previous} ${converted === 1 ? 'link is' : 'links are'} now ${metal}.`
            : ''
        );
      });

      this.els.sizes.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-size]');
        if (!btn) return;
        const type = this.type;
        const links = Number(btn.dataset.size);
        if (links === this.build.length) return;
        const choice = type.choices.find((c) => c.links === links);
        if (!choice) return;
        // Ask only when charms would come off; plain links are re-added with one tap.
        const lost = this.build.slots.filter(Boolean).slice(links).filter((key) => !plainMetalOf(key)).length;
        if (lost && !window.confirm(`This size removes ${lost} charm${lost === 1 ? '' : 's'} from the end. Resize bracelet?`)) return;
        this.build.length = links;
        const removed = this.fitToBase(type, this.build);
        if (this.selected != null && this.selected >= links) this.selected = null;
        this.renderSizes();
        this.afterChange(
          removed
            ? `${sizeText(choice)} selected, so the last ${removed} ${removed === 1 ? 'link was' : 'links were'} taken off.`
            : `${sizeText(choice)} selected.`
        );
      });

      add.addEventListener('click', () => this.addToCart());
      this.addEventListener('click', (e) => { if (e.target.closest('[data-cb-fill-plain]')) this.fillPlain(); });
      barAdd.addEventListener('click', () => this.addToCart());
      reset.addEventListener('click', () => {
        if (this.type.flexible) this.build.slots = [];
        else this.build.slots.fill(null);
        this.selected = null;
        this.afterChange(`${this.type.label} cleared.`);
      });

      this.bindDragAndDrop();
    }

    setCharmDragImage(event, source) {
      const image = source.querySelector('img');
      if (!image || !event.dataTransfer.setDragImage) return;
      const target = this.els.preview.querySelector('[data-slot]');
      const size = Math.max(36, Math.min(52, target?.getBoundingClientRect().width || 44));
      const ghost = document.createElement('div');
      ghost.className = 'cb-drag-ghost';
      ghost.style.width = `${size}px`;
      ghost.style.height = `${size}px`;
      const photo = image.cloneNode();
      photo.removeAttribute('class');
      photo.removeAttribute('style');
      photo.removeAttribute('loading');
      ghost.appendChild(photo);
      document.body.appendChild(ghost);
      event.dataTransfer.setDragImage(ghost, size / 2, size / 2);
      // Native drag imagery is captured at dragstart; remove the temporary DOM copy.
      setTimeout(() => ghost.remove(), 0);
    }

    bindDragAndDrop() {
      const { grid, preview } = this.els;
      let dragging = null;
      let scrollFrame = null, scrollBox = null, scrollSpeed = 0;
      const stopScroll = () => {
        if (scrollFrame != null) cancelAnimationFrame(scrollFrame);
        scrollFrame = null; scrollBox = null; scrollSpeed = 0;
      };
      const stepScroll = () => {
        if (!scrollBox || !scrollSpeed || !dragging) return stopScroll();
        scrollBox.scrollLeft += scrollSpeed;
        scrollFrame = requestAnimationFrame(stepScroll);
      };
      this.stopDragScroll = stopScroll;
      preview.addEventListener('dragleave', (event) => {
        if (!preview.contains(event.relatedTarget)) {
          stopScroll();
          preview.querySelectorAll('.is-drop-target').forEach(el => el.classList.remove('is-drop-target'));
        }
      });

      grid.addEventListener('dragstart', (e) => {
        const tile = e.target.closest('[data-key], [data-plain-link]');
        if (!tile || tile.getAttribute('aria-disabled') === 'true') return e.preventDefault();
        dragging = tile.hasAttribute('data-plain-link') ? { kind: 'plain', metal: tile.dataset.plainMetal } : { kind: 'charm', key: tile.dataset.key };
        this.setCharmDragImage(e, tile);
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData('text/plain', tile.dataset.key || plainToken(dragging.metal));
        this.classList.add('cb--dragging');
      });

      preview.addEventListener('dragstart', (e) => {
        const link = e.target.closest('[data-slot]');
        if (!link || !this.build.slots[Number(link.dataset.slot)]) return e.preventDefault();
        dragging = { kind: 'slot', index: Number(link.dataset.slot) };
        this.setCharmDragImage(e, link);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', link.dataset.slot);
        this.classList.add('cb--dragging');
        this.dragOffController?.abort();
        this.dragOffController = new AbortController();
        const options = { signal: this.dragOffController.signal };
        const overDesign = (target) => preview.contains(target) && target.closest('.cb__flat-chain, .cb__keychain-object, .cb__link-editor');
        document.addEventListener('dragover', (event) => {
          if (dragging?.kind !== 'slot' || overDesign(event.target)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
        }, options);
        document.addEventListener('drop', (event) => {
          if (dragging?.kind !== 'slot') return;
          event.preventDefault();
          if (!overDesign(event.target)) this.removeAt(dragging.index);
          end();
        }, options);
      });

      const end = () => {
        stopScroll();
        this.dragOffController?.abort();
        dragging = null;
        this.classList.remove('cb--dragging', 'cb--removing');
        this.querySelectorAll('.is-drop-target').forEach((el) => el.classList.remove('is-drop-target'));
      };
      this.addEventListener('dragend', end);

      preview.addEventListener('dragover', (e) => {
        if (dragging) {
          const scroller = e.target.closest('.cb__flat-scroll, .cb__product-scroll');
          if (scroller && scroller.scrollWidth > scroller.clientWidth) {
            const rect = scroller.getBoundingClientRect();
            scrollBox = scroller;
            scrollSpeed = e.clientX < rect.left + 36 ? -8 : e.clientX > rect.right - 36 ? 8 : 0;
            if (scrollSpeed && scrollFrame == null) scrollFrame = requestAnimationFrame(stepScroll);
            if (!scrollSpeed) stopScroll();
          } else stopScroll();
          preview.querySelectorAll('.is-drop-target').forEach(el => el.classList.remove('is-drop-target'));
        }
        const link = e.target.closest('[data-slot], [data-cb-append]');
        if (!dragging || !link || (link.hasAttribute('data-cb-append') && dragging.kind === 'slot')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = dragging.kind === 'slot' ? 'move' : 'copy';
        preview.querySelectorAll('.is-drop-target').forEach((el) => el !== link && el.classList.remove('is-drop-target'));
        link.classList.add('is-drop-target');
      });

      preview.addEventListener('drop', (e) => {
        const link = e.target.closest('[data-slot], [data-cb-append]');
        if (!dragging || !link || (link.hasAttribute('data-cb-append') && dragging.kind === 'slot')) return;
        e.preventDefault();
        this.dropOn(dragging, link);
        end();
      });

      this.bindTouchDrag();
    }

    // Mouse and touch drags end the same way: a charm or plain link lands on that link (or the
    // keychain's next position); a dragged link swaps with the link it is dropped on.
    dropOn(drag, link) {
      const to = link.hasAttribute('data-cb-append') ? this.build.slots.length : Number(link.dataset.slot);
      if (drag.kind === 'charm') this.place(drag.key, to);
      else if (drag.kind === 'plain') this.placePlain(to, drag.metal);
      else this.move(drag.index, to, true);
    }

    // The link (or keychain's next position) a drag can drop on at this point, or null.
    dropTarget(drag, el) {
      const link = el && this.els.preview.contains(el) ? el.closest('[data-slot], [data-cb-append]') : null;
      if (!link || (drag.kind === 'slot' && link.hasAttribute('data-cb-append'))) return null;
      return link;
    }

    hitTest(x, y) {
      return typeof document.elementFromPoint === 'function' ? document.elementFromPoint(x, y) : null;
    }

    /* Touch drag. Native drag and drop is off on touch screens (it fights scrolling), so a press and
       hold lifts a charm, plain link or placed link instead: a finger that moves first scrolls as
       usual; after the hold the page stops scrolling, a copy follows the finger, the link under it
       highlights, and lifting places it. A placed link dropped off the design is removed, as on
       desktop. A hold released without moving acts as a tap. Tap and buttons keep working. */
    bindTouchDrag() {
      const { grid, preview } = this.els;
      this.holdDelay = 250;
      let pending = null;
      let drag = null;
      let frame = null;
      let suppressUntil = 0;
      const slop = 8;
      const docOptions = { capture: true };
      const sourceOf = (target) => {
        const tile = grid.contains(target) && target.closest('[data-key], [data-plain-link]');
        if (tile && tile.getAttribute('aria-disabled') !== 'true') {
          return tile.hasAttribute('data-plain-link') ? { kind: 'plain', metal: tile.dataset.plainMetal, el: tile } : { kind: 'charm', key: tile.dataset.key, el: tile };
        }
        const link = preview.contains(target) && target.closest('[data-slot]');
        if (link && this.build.slots[Number(link.dataset.slot)]) return { kind: 'slot', index: Number(link.dataset.slot), el: link };
        return null;
      };
      const clearTargets = () => preview.querySelectorAll('.is-drop-target').forEach((el) => el.classList.remove('is-drop-target'));
      // A finger is less precise than a mouse: anywhere on the design's area keeps a dragged link.
      const overDesign = (el) => !!el && preview.contains(el);
      const raf = (fn) => (typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : setTimeout(fn, 16));
      const unraf = (id) => (typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame(id) : clearTimeout(id));
      const stopListening = () => {
        document.removeEventListener('pointermove', onMove, docOptions);
        document.removeEventListener('pointerup', onUp, docOptions);
        document.removeEventListener('pointercancel', onCancel, docOptions);
      };
      const finish = () => {
        clearTimeout(pending?.timer);
        pending = null;
        if (frame != null) unraf(frame);
        frame = null;
        if (drag) {
          drag.ghost.remove();
          drag.el.classList.remove('is-lifted');
          this.classList.remove('cb--dragging');
          clearTargets();
        }
        drag = null;
        this.touchDragging = false;
        stopListening();
      };
      this.cancelTouchDrag = finish;
      // Keeps the finger's position and, near the design's edges, scrolls the links sideways
      // (tablets, whose design is not pinned, also scroll the page near the top edge).
      const step = () => {
        frame = null;
        if (!drag) return;
        const { x, y } = drag;
        const size = drag.size;
        drag.ghost.style.transform = `translate3d(${Math.round(x - size / 2)}px, ${Math.round(y - size - 28)}px, 0)`;
        const scroller = preview.querySelector('.cb__flat-scroll, .cb__product-scroll');
        let moving = false;
        if (scroller && scroller.scrollWidth > scroller.clientWidth) {
          const box = scroller.getBoundingClientRect();
          if (y >= box.top - 24 && y <= box.bottom + 24) {
            const speed = x < box.left + 40 ? -8 : x > box.right - 40 ? 8 : 0;
            if (speed) { scroller.scrollLeft += speed; moving = true; }
          }
        }
        if (!this.app && y < 56) { window.scrollBy(0, -10); moving = true; }
        const el = this.hitTest(x, y);
        const target = this.dropTarget(drag, el);
        clearTargets();
        target?.classList.add('is-drop-target');
        drag.target = target;
        drag.ghost.classList.toggle('is-removing', drag.kind === 'slot' && !overDesign(el));
        if (moving) frame = raf(step);
      };
      const begin = () => {
        const source = pending;
        pending = null;
        if (!source?.el.isConnected) return finish();
        const size = 52;
        const ghost = document.createElement('div');
        ghost.className = 'cb-drag-ghost cb-drag-ghost--touch';
        ghost.style.width = `${size}px`;
        ghost.style.height = `${size}px`;
        const image = source.el.querySelector('img');
        if (image) {
          const photo = image.cloneNode();
          ['class', 'style', 'loading', 'srcset', 'sizes'].forEach((name) => photo.removeAttribute(name));
          ghost.appendChild(photo);
        }
        document.body.appendChild(ghost);
        drag = { ...source, ghost, size, x: source.x, y: source.y, moved: false, target: null };
        this.touchDragging = true;
        source.el.classList.add('is-lifted');
        this.classList.add('cb--dragging');
        // The design must be on screen to drop on it.
        if (this.hasAttribute('data-collapsed')) this.els.collapse.click();
        navigator.vibrate?.(8);
        step();
      };
      const onMove = (e) => {
        if (e.pointerId !== (drag || pending)?.pointerId) return;
        if (pending) {
          if (Math.hypot(e.clientX - pending.x, e.clientY - pending.y) > slop) finish();
          return;
        }
        if (Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0) > slop) drag.moved = true;
        drag.x = e.clientX;
        drag.y = e.clientY;
        if (frame == null) frame = raf(step);
      };
      const onUp = (e) => {
        if (e.pointerId !== (drag || pending)?.pointerId) return;
        if (!drag) return finish();
        const current = drag;
        current.x = e.clientX;
        current.y = e.clientY;
        const el = this.hitTest(current.x, current.y);
        const target = this.dropTarget(current, el);
        finish();
        if (!current.moved) {
          // A long press released in place is a slow tap, acted on here. The browser may still send
          // its own click for it, which would repeat the tap or land on the sheet that just opened;
          // ignore that one click. A finger that moved sends no click, so nothing is held back then.
          suppressUntil = Date.now() + 400;
          if (current.kind === 'slot') this.select(current.index);
          else if (current.kind === 'plain') {
            if (this.app) this.openSheet(plainToken(current.metal), current.el); else this.placePlain(undefined, current.metal);
          }
          else if (this.app) this.openSheet(current.key, current.el);
          else this.place(current.key);
        } else if (target) {
          this.dropOn(current, target);
          this.dismissCoach();
        } else if (current.kind === 'slot' && !overDesign(el)) this.removeAt(current.index);
      };
      const onCancel = (e) => {
        if (e.pointerId === (drag || pending)?.pointerId) finish();
      };
      this.addEventListener('pointerdown', (e) => {
        if (!this.touch || e.pointerType === 'mouse' || !e.isPrimary || pending || drag || this.busy) return;
        const source = sourceOf(e.target);
        if (!source) return;
        pending = { ...source, pointerId: e.pointerId, x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY };
        pending.timer = setTimeout(begin, this.holdDelay);
        document.addEventListener('pointermove', onMove, docOptions);
        document.addEventListener('pointerup', onUp, docOptions);
        document.addEventListener('pointercancel', onCancel, docOptions);
      });
      // Once a drag has begun the finger moves the charm, not the page. Registered up front and not
      // passive, so the browser lets it stop scrolling; it does nothing unless a drag is active.
      this.addEventListener('touchmove', (e) => { if (drag && e.cancelable) e.preventDefault(); }, { passive: false });
      this.addEventListener('contextmenu', (e) => { if (pending || drag) e.preventDefault(); });
      this.addEventListener('click', (e) => {
        if (Date.now() > suppressUntil) return;
        suppressUntil = 0;
        e.preventDefault();
        e.stopImmediatePropagation();
      }, true);
    }

    /* ----- actions ----- */

    // Places a plain link in either finish. Bracelets: the given or selected link, else the next empty
    // one; with none empty, the shopper picks a link (plainMode). Keychains: replaces the given or
    // selected link, else adds a spacer at the end within the link limit. Returns true when it placed a
    // link or started (or canceled) choosing one, false when refused.
    placePlain(index = this.selected, metal = this.baseMetal()) {
      if (this.busy || !this.type || !PLAIN_METALS.includes(metal)) return false;
      const build = this.build;
      if (this.type.flexible) {
        if (index == null || index >= build.slots.length) {
          if (build.slots.length >= this.maxCharms()) {
            this.announce(this.limitMessage(), true);
            return false;
          }
          index = build.slots.length;
        }
      } else {
        if (index == null && !this.plainMode) {
          // Like a charm tile: fill the next empty link. Choosing a position only when none is empty.
          const next = build.slots.findIndex((key) => !key);
          if (next !== -1) index = next;
        }
        if (index == null) {
          // A second tap on the same finish cancels the picker; the other finish switches it.
          const canceling = this.plainMode && this.plainMetal === metal;
          const problem = !canceling && this.plainProblem(metal);
          if (problem) { this.announce(problem, true); return false; }
          this.plainMode = !canceling;
          this.plainMetal = metal;
          this.updateBadges();
          this.announce(this.plainMode ? `Select a position on your bracelet to add a plain ${metal} link.` : 'Plain link selection canceled.', this.plainMode);
          // On phones the design is pinned in view already; elsewhere bring it on screen.
          if (!this.app) this.els.preview.scrollIntoView?.({ block: 'nearest', behavior: this.reducedMotion() ? 'auto' : 'smooth' });
          return true;
        }
      }
      if (!Number.isInteger(index) || index < 0 || index > build.slots.length || (!this.type.flexible && index === build.slots.length)) return false;
      const problem = this.plainProblem(metal, index);
      if (problem) { this.announce(problem, true); return false; }
      this.plainMode = false;
      build.slots[index] = this.plainKey(metal);
      this.selected = null;
      this.afterChange(`Plain ${metal} link added at position ${index + 1}.`);
      this.revealSlot(index);
      return true;
    }

    reducedMotion() {
      return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    }

    // Brings a changed link into view inside the design's own horizontal scroller (never the page) and
    // marks it briefly, so a shopper who placed it from the grid sees where it went.
    revealSlot(index) {
      const link = this.els.preview.querySelector(`[data-slot="${index}"]`);
      const scroller = link?.closest('.cb__flat-scroll, .cb__product-scroll');
      if (!link) return;
      link.classList.add('is-new');
      if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
      const box = scroller.getBoundingClientRect();
      const rect = link.getBoundingClientRect();
      const margin = Math.min(48, box.width / 4);
      let left = scroller.scrollLeft;
      if (rect.left < box.left + margin) left -= box.left + margin - rect.left;
      else if (rect.right > box.right - margin) left += rect.right - (box.right - margin);
      if (left === scroller.scrollLeft) return;
      if (typeof scroller.scrollTo === 'function') scroller.scrollTo({ left, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
      else scroller.scrollLeft = left;
    }

    limitMessage() {
      return `Keychains hold up to ${this.maxCharms()} links, counting charms and plain links. Select a link to swap it.`;
    }

    // Where a charm would go, or why it can't: the given or selected link, else the next empty link;
    // keychains add at the end within the link limit; a full bracelet gives up its first plain link.
    placement(key, index = null) {
      const charm = this.charmMap.get(key);
      const build = this.build;
      let target = index ?? this.selected;
      if (target == null) target = build.slots.findIndex((k) => !k);
      if (target === -1 && this.type.flexible) target = build.slots.length;
      if (this.type.flexible && target >= build.slots.length && build.slots.length >= this.maxCharms()) return { problem: this.limitMessage() };
      // Every link is chosen: a tapped charm takes the first plain link, so "Fill all" never traps the shopper.
      if (target === -1 && !this.type.flexible) {
        target = build.slots.indexOf(plainToken(this.baseMetal()));
        if (target === -1) target = build.slots.findIndex((k) => plainMetalOf(k));
      }
      if (target === -1) return { problem: `All ${build.slots.length} links hold charms. Select a link to swap its charm.` };
      if (charm.max != null && this.countOf(key, target) >= this.remaining(charm)) {
        return { problem: `Only ${charm.max} ${charm.name} ${charm.max === 1 ? 'is' : 'are'} in stock.` };
      }
      return { target };
    }

    // Returns true when the charm was placed.
    place(key, index = null) {
      if (this.busy) return false;
      const charm = this.charmMap.get(key);
      const build = this.build;
      if (!charm || !charm.available || !this.type) return false;
      const { target, problem } = this.placement(key, index);
      if (problem) {
        this.announce(problem, true);
        return false;
      }
      this.plainMode = false;
      build.slots[target] = key;
      this.selected = null;
      this.afterChange(`${charm.name} placed on link ${target + 1}.`);
      this.revealSlot(target);
      return true;
    }

    /* ----- charm details (phones) ----- */

    // Where a plain link would go, or why it can't. Bracelets with no empty link and none selected
    // ask the shopper to choose a link (`choose`).
    plainPlacement(metal) {
      const build = this.build;
      const index = this.selected;
      if (this.type.flexible) {
        if (index == null && build.slots.length >= this.maxCharms()) return { problem: this.limitMessage() };
        const problem = this.plainProblem(metal, index ?? -1);
        return problem ? { problem } : { target: index ?? build.slots.length };
      }
      const target = index ?? build.slots.findIndex((k) => !k);
      const problem = this.plainProblem(metal, target);
      if (problem) return { problem };
      return target === -1 ? { choose: true } : { target };
    }

    // Phones show image-only tiles, so a tap opens the charm's (or plain link's) details with one clear
    // action. `key` is a charm key or a plain-link token ('plain:silver').
    openSheet(key, opener = null) {
      const plain = plainMetalOf(key);
      const charm = !plain && this.charmMap.get(key);
      const sheet = this.els.sheet;
      if (!sheet || (plain ? !this.plainVariant(plain) : !charm || !charm.available)) return;
      this.sheetKey = key;
      this.sheetOpener = opener;
      this.renderSheet();
      if (!sheet.hasAttribute('open')) {
        try { sheet.showModal(); } catch { sheet.setAttribute('open', ''); }
      }
      this.focusQuietly(sheet.querySelector('[data-cb-sheet-add]:not([disabled])') || sheet.querySelector('[data-cb-sheet-close]'));
    }

    renderSheet() {
      const plain = plainMetalOf(this.sheetKey);
      const item = plain ? this.plainVariant(plain) : this.sheetKey && this.charmMap.get(this.sheetKey);
      if (!item) return;
      const type = this.type;
      const { target, problem, choose } = plain ? this.plainPlacement(plain) : this.placement(this.sheetKey);
      const used = this.build.slots.flatMap((k, i) => ((plain ? plainMetalOf(k) === plain : k === this.sheetKey) ? [i + 1] : []));
      const left = !item.available ? 0 : item.max == null ? Infinity : this.remaining(item) - used.length;
      const stock = !item.available ? 'Sold out' : left <= 0 ? 'All available are on your design' : left <= 3 ? `Only ${left} left` : 'In stock';
      const action = this.selected != null ? `Add to link ${this.selected + 1}` : choose ? 'Choose a link for it' : `Add to ${type.label.toLowerCase()}`;
      const finish = plain || (item.metal && item.metal !== 'both' ? item.metal : '');
      const photo = plain ? this.plainImage(720, plain) : item.image && sizedImage(item.image, 720);
      const name = plain ? `Plain ${metalLabel(plain)} link` : item.name;
      const where = used.length > 4 ? `${used.length} links` : `link${used.length > 1 ? 's' : ''} ${used.join(', ')}`;
      this.els.sheetBody.innerHTML = `
        <button type="button" class="cb__sheet-close" data-cb-sheet-close aria-label="Close"><svg aria-hidden="true" viewBox="0 0 20 20" width="20" height="20"><path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></button>
        ${photo ? `<img class="cb__sheet-photo${plain ? ' is-plain' : ''}" src="${esc(photo)}" alt="" width="280" height="280">` : ''}
        <h2 class="cb__sheet-name" id="cb-sheet-title-${esc(this.dataset.sectionId)}">${esc(name)}</h2>
        ${finish ? `<p class="cb__sheet-meta"><span class="cb__swatch cb__swatch--${esc(finish)}" aria-hidden="true"></span> ${metalLabel(finish)}</p>` : ''}
        <p class="cb__sheet-price">${esc(this.formatMoney(item.price))}</p>
        <p class="cb__sheet-stock${left <= 3 ? ' is-low' : ''}">${stock}${used.length ? ` · On your design: ${where}` : ''}</p>
        <button type="button" class="button button--full-width cb__sheet-add" data-cb-sheet-add${problem ? ' disabled' : ''}>${esc(action)}</button>
        ${problem ? `<p class="cb__sheet-note" role="status">${esc(problem)}</p>` : target != null && this.selected == null ? `<p class="cb__sheet-hint">Goes on link ${target + 1}</p>` : ''}`;
    }

    // Closing returns focus to the tile without moving the grid.
    closeSheet() {
      const sheet = this.els.sheet;
      if (sheet?.hasAttribute('open')) {
        if (typeof sheet.close === 'function') sheet.close(); else sheet.removeAttribute('open');
      }
      this.sheetClosed();
    }

    sheetClosed() {
      if (this.sheetKey == null) return;
      this.sheetKey = null;
      const opener = this.sheetOpener;
      this.sheetOpener = null;
      if (opener?.isConnected) opener.focus?.({ preventScroll: true });
    }

    removeAt(index) {
      if (this.busy) return;
      if (index == null || !this.build.slots[index]) return;
      const plain = plainMetalOf(this.build.slots[index]);
      if (this.type.flexible) this.build.slots.splice(index, 1);
      else this.build.slots[index] = null;
      this.selected = null;
      this.afterChange(this.type.flexible ? `${plain ? 'Plain link' : 'Charm'} removed. Your keychain is now shorter.` : `Link ${index + 1} is now empty. Add a charm or a plain link.`);
      if (this.build.slots.length) this.focusSlot(Math.min(index, this.build.slots.length - 1));
    }

    // The plain-link product's own photo in that finish, so tiles match the cart and checkout;
    // the bundled blank-link art covers keychain placeholders and a listing without photos.
    plainImage(size, metal = this.baseMetal()) {
      const item = this.plainVariant(metal);
      return item?.image ? sizedImage(item.image, size) : this.config.blankImages[metal];
    }

    // Most links between a keychain's two fixed end links, charms and plain links together.
    maxCharms() {
      return this.type?.maxCharms > 0 ? this.type.maxCharms : Infinity;
    }

    // Every bracelet position must be chosen; this fills the remaining empty ones with the finish's plain link.
    fillPlain() {
      if (this.busy || !this.type || this.type.flexible) return;
      const metal = this.baseMetal();
      const empty = this.build.slots.flatMap((key, i) => (!key ? [i] : []));
      if (!empty.length) return;
      this.plainMode = false;
      empty.forEach((i) => { this.build.slots[i] = plainToken(metal); });
      this.selected = null;
      this.afterChange(`${empty.length} plain ${metal} ${empty.length === 1 ? 'link' : 'links'} added.`);
      // The next step is the cart button: the price sheet's on desktop, the bar's on phones.
      this.focusQuietly(this.app ? this.els.barAdd : this.els.add);
    }

    move(from, to, swap = false) {
      if (this.busy) return;
      const slots = this.build.slots;
      if (from == null || to < 0 || to >= slots.length || from === to) return;
      if (swap || Math.abs(from - to) === 1) {
        [slots[from], slots[to]] = [slots[to], slots[from]];
      }
      this.selected = swap ? null : to;
      this.afterChange(`Moved to link ${to + 1}.`);
      if (!swap) this.focusSlot(to);
    }

    select(index) {
      if (this.type.flexible && index != null && index >= this.build.slots.length) {
        this.selected = null;
        this.focusQuietly(this.els.grid.querySelector('[data-key]:not([hidden])'));
        return;
      }
      if (this.plainMode && index != null && !this.type.flexible) return this.placePlain(index, this.plainMetal);
      this.selected = index === this.selected ? null : index;
      this.renderPreview();
      this.renderDetail();
      if (this.selected != null) {
        this.focusSlot(this.selected);
        // Phones show tiles without names, so the bar says what the selected link holds.
        if (this.app) this.showBarMessage(this.slotSummary(this.selected));
      }
    }

    // "Link 4: Crystal Tulip, $8.00" / "Link 4: plain silver" / "Link 4: empty"
    slotSummary(i) {
      const key = this.build.slots[i];
      const plain = plainMetalOf(key);
      const charm = key && !plain && this.charmMap.get(key);
      return `Link ${i + 1}: ${charm ? `${charm.name}, ${this.formatMoney(charm.price)}` : plain ? `plain ${plain}` : key ? 'unavailable' : 'empty'}`;
    }

    focusSlot(index) {
      const link = this.els.preview.querySelector(`[data-slot="${index}"]`);
      if (link) link.focus({ preventScroll: true });
    }

    clearFilters() {
      this.filters = { metal: 'all', category: 'all', query: '' };
      this.els.search.value = '';
      this.els.metal.querySelectorAll('[data-metal]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.metal === 'all')));
      this.els.categories
        .querySelectorAll('[data-category]')
        .forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.category === 'all')));
      this.applyFilters();
    }

    afterChange(message) {
      const next = this.snapshot();
      if (next !== this.lastState) {
        this.history.push(this.lastState);
        if (this.history.length > 50) this.history.shift();
        this.future = [];
        this.lastState = next;
      }
      this.save();
      this.renderPreview();
      this.renderDetail();
      this.renderSummary();
      this.updateBadges();
      this.updateHistory();
      if (message) this.announce(message);
    }

    snapshot() {
      return JSON.stringify({ activeType: this.activeType, builds: this.builds });
    }

    updateHistory() {
      this.els.undo.disabled = !this.history.length;
      this.els.redo.disabled = !this.future.length;
    }

    travelHistory(redo) {
      if (this.busy) return;
      const source = redo ? this.future : this.history;
      if (!source.length) return;
      (redo ? this.history : this.future).push(this.snapshot());
      this.lastState = source.pop();
      const state = JSON.parse(this.lastState);
      this.builds = state.builds;
      this.activeType = state.activeType;
      this.selected = null;
      this.save();
      this.renderAll();
      this.updateHistory();
      this.announce(redo ? 'Change restored.' : 'Last change undone.');
    }

    announce(message, visible = false) {
      this.els.feedback.hidden = !visible;
      this.els.feedback.textContent = message;
      // One polite live region; clearing first lets an identical message be read again.
      this.els.live.textContent = '';
      const setLive = () => { this.els.live.textContent = message; };
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(setLive); else setLive();
      // The phone bar shows every change for a moment ("Crystal Tulip placed on link 3."), because the
      // design itself may be out of view; screen readers hear it once, from the live region above.
      this.showBarMessage(message);
    }

    showBarMessage(message) {
      this.els.barStatus.textContent = message;
      this.els.bar.classList.add('has-message');
      clearTimeout(this.statusTimer);
      this.statusTimer = setTimeout(() => {
        this.els.bar.classList.remove('has-message');
        if (this.type?.baseMap) { const {charms, blanks} = this.totals(); this.els.barStatus.textContent = this.barStatus(charms, blanks); }
      }, this.statusDelay);
    }

    showError(message) {
      this.els.error.textContent = message;
      this.els.error.hidden = !message;
      // On phones the error sits in the price sheet; open it so the message is seen.
      if (message && this.app) this.setOrderOpen(true);
    }

    // Phones: the price breakdown, plain-link fill and cart messages open as a sheet above the bar.
    setOrderOpen(open) {
      const value = !!open && !!this.app;
      this.toggleAttribute('data-order-open', value);
      this.els.barMore?.setAttribute('aria-expanded', String(value));
    }

    /* ----- charm browser ----- */

    renderCategories() {
      const used = new Set(this.charms.filter((c) => c.available).flatMap((c) => c.collections));
      const cats = this.categories.filter((c) => used.has(c.handle));
      if (!cats.length) {
        this.els.categories.hidden = true;
        return;
      }
      this.els.categories.innerHTML = [
        `<button type="button" class="cb__chip" data-category="all" aria-pressed="true">All themes</button>`,
        ...cats.map(
          (c) =>
            `<button type="button" class="cb__chip" data-category="${esc(c.handle)}" aria-pressed="false">${esc(c.label)}</button>`
        ),
      ].join('');
    }

    buildGrid() {
      const grid = this.els.grid;
      grid.removeAttribute('aria-busy');
      grid.innerHTML = '';
      this.tiles = new Map();
      const frag = document.createDocumentFragment();
      // Plain links in both finishes, pinned first for bracelets and keychains alike.
      this.plainTiles = {};
      PLAIN_METALS.forEach((metal) => {
        const tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'cb__tile cb__plain-tile';
        tile.setAttribute('data-plain-link', '');
        tile.dataset.plainMetal = metal;
        tile.setAttribute('draggable', String(this.nativeDrag));
        tile.innerHTML = `<span class="cb__tile-img"><img data-plain-image alt="" draggable="false"></span><span class="cb__tile-name">Plain ${metalLabel(metal)} link</span><span class="cb__tile-add">+ Add plain link</span><span class="cb__tile-meta"></span>`;
        this.plainTiles[metal] = tile;
        frag.appendChild(tile);
      });

      this.charms.filter((c) => c.available).forEach((c) => {
        const tile = document.createElement('div');
        tile.className = 'cb__tile';
        tile.dataset.key = c.key;
        tile.setAttribute('role', 'button');
        tile.setAttribute('tabindex', c.available ? '0' : '-1');
        tile.setAttribute('draggable', String(c.available && this.nativeDrag));
        if (!c.available) tile.setAttribute('aria-disabled', 'true');
        const metal = c.metal && c.metal !== 'both' ? c.metal : null;
        tile.setAttribute(
          'aria-label',
          `${c.name}${metal ? `, ${metalLabel(metal)}` : ''}, ${c.available ? this.formatMoney(c.price) : 'sold out'}`
        );
        tile.innerHTML = `
          <span class="cb__tile-img">${
            c.image
              ? `<img src="${esc(sizedImage(c.image, 240))}" srcset="${esc(sizedImage(c.image, 240))} 240w, ${esc(sizedImage(c.image, 480))} 480w" sizes="(max-width: 749px) 34vw, 180px" alt="" loading="lazy" width="120" height="120" draggable="false">`
              : ''
          }</span>
          <span class="cb__tile-name">${esc(c.name)}</span>
          <span class="cb__tile-add" aria-hidden="true">${c.available ? '+ Add charm' : 'Sold out'}</span>
          <span class="cb__tile-meta">
            ${metal ? `<span class="cb__tile-metal"><span class="cb__swatch cb__swatch--${metal}" aria-hidden="true"></span>${metalLabel(metal)}</span>` : '<span></span>'}
            <span class="cb__tile-price">${c.available ? esc(this.formatMoney(c.price)) : 'Sold out'}</span>
          </span>
          <span class="cb__tile-badge" hidden></span>`;
        this.tiles.set(c.key, tile);
        frag.appendChild(tile);
      });

      this.emptyEl = document.createElement('div');
      this.emptyEl.className = 'cb__empty';
      this.emptyEl.hidden = true;
      this.emptyEl.innerHTML = `<p>No in-stock charms match these filters.</p><button type="button" class="button button--secondary" data-cb-clear-filters>Show all charms</button>`;
      frag.appendChild(this.emptyEl);
      grid.appendChild(frag);
    }

    matches(c) {
      if (!c.available) return false;
      const f = this.filters;
      if (f.metal !== 'all' && c.metal !== f.metal && c.metal !== 'both') return false;
      if (f.category !== 'all' && !c.collections.includes(f.category)) return false;
      if (f.query && !f.query.split(/\s+/).every((word) => c.haystack.includes(word))) return false;
      return true;
    }

    applyFilters() {
      if (!this.tiles) return;
      let shown = 0;
      this.charms.forEach((c) => {
        const visible = this.matches(c);
        if (this.tiles.has(c.key)) this.tiles.get(c.key).hidden = !visible;
        if (visible) shown += 1;
      });
      this.emptyEl.hidden = shown > 0;
      this.emptyEl.querySelector('p').textContent = this.charms.some((c) => c.available) ? 'No in-stock charms match these filters.' : 'No charms are in stock right now. You can still design a plain bracelet.';
      this.els.categories.querySelectorAll('[data-category]').forEach((button) => {
        const handle = button.dataset.category;
        const count = this.charms.filter((c) => c.available && (handle === 'all' || c.collections.includes(handle))).length;
        const label = handle === 'all' ? 'All charms' : this.categories.find((c) => c.handle === handle)?.label || handle;
        button.textContent = `${label} (${count})`;
        button.setAttribute('aria-pressed', String(handle === this.filters.category));
      });
      this.els.count.textContent = `${shown} ${this.filters.metal === 'all' ? '' : this.filters.metal + ' '}${shown === 1 ? 'charm' : 'charms'}`;
      this.renderBrowsePicks();
      this.updateBadges();
    }

    updateBadges() {
      if (!this.tiles) return;
      const counts = new Map();
      this.build.slots.forEach((k) => k && counts.set(k, (counts.get(k) || 0) + 1));
      // Pinned first in every category, search and metal filter, for bracelets and keychains. A finish
      // without a configured plain-link variant has no tile; a sold-out one explains instead of adding.
      Object.entries(this.plainTiles || {}).forEach(([metal, tile]) => {
        const item = this.plainVariant(metal);
        tile.hidden = !item;
        if (!item) return;
        const used = this.plainCount(metal);
        const soldOut = !item.available;
        const exhausted = !soldOut && used >= this.remaining(item);
        const picking = !!this.plainMode && this.plainMetal === metal;
        const price = this.formatMoney(item.price);
        tile.querySelector('.cb__tile-meta').innerHTML = `<span class="cb__tile-metal"><span class="cb__swatch cb__swatch--${metal}" aria-hidden="true"></span>${metalLabel(metal)}</span><span class="cb__tile-price">${soldOut ? 'Sold out' : esc(price)}</span>`;
        tile.querySelector('[data-plain-image]').src = this.plainImage(240, metal);
        tile.setAttribute('aria-pressed', String(picking));
        tile.setAttribute('aria-disabled', String(soldOut || exhausted));
        tile.setAttribute('draggable', String(!(soldOut || exhausted) && this.nativeDrag));
        tile.setAttribute('aria-label', `Plain ${metal} link, ${soldOut ? 'sold out' : price}. ${this.type.flexible ? 'Adds a plain spacer link to your keychain.' : 'Adds a plain link to the next empty position.'}`);
        tile.querySelector('.cb__tile-add').textContent = picking ? 'Choose a link above · Cancel' : soldOut ? 'Sold out' : exhausted ? 'All available added' : '+ Add plain link';
      });
      this.tiles.forEach((tile, key) => {
        const badge = tile.querySelector('.cb__tile-badge');
        const n = counts.get(key) || 0;
        badge.hidden = n === 0;
        badge.textContent = n > 1 ? `On ${n} links` : 'On your design';
        tile.classList.toggle('is-used', n > 0);
        const c = this.charmMap.get(key);
        const exhausted = n >= this.remaining(c);
        tile.querySelector('.cb__tile-add').textContent = exhausted ? 'All available added' : '+ Add charm';
        tile.setAttribute('aria-disabled', String(exhausted));
        tile.setAttribute('draggable', String(!exhausted && this.nativeDrag));
      });
    }

    // Mouse and trackpad drag natively; touch screens use taps (a native touch drag fights scrolling).
    get nativeDrag() {
      return !this.touch;
    }

    /* ----- design panel ----- */

    renderAll() {
      if (!this.type) return;
      this.renderTypes();
      this.renderBases();
      this.renderSizes();
      this.renderOptions();
      this.renderBrowsePicks();
      this.renderPreview();
      this.renderDetail();
      this.renderSummary();
      this.updateBadges();
    }

    // Keeps a native select's options and value in step without rebuilding it while a picker may be open.
    syncSelect(select, options, value) {
      const same = select.options.length === options.length && options.every((o, i) => select.options[i].value === o.value);
      if (!same) select.innerHTML = options.map((o) => `<option value="${esc(o.value)}"></option>`).join('');
      options.forEach((o, i) => {
        const option = select.options[i];
        if (option.textContent !== o.text) option.textContent = o.text;
        option.disabled = !!o.disabled;
      });
      select.value = value;
      // The closed pill may show a shorter label (`short`); the phone's menu lists the full text.
      const shown = select.closest('.cb__pick')?.querySelector('.cb__pick-value');
      const current = options[select.selectedIndex];
      if (shown) shown.textContent = current ? current.short || current.text : '';
    }

    // A pill that shows "Size / 7–7.5 in · 18 links" and opens the phone's own picker anywhere it is tapped.
    pickHtml(name, label) {
      return `<label class="cb__pick cb__pick--${name}" data-pick="${name}"><span class="cb__pick-label">${esc(label)}<svg class="cb__pick-chevron" aria-hidden="true" viewBox="0 0 20 20" width="12" height="12"><path d="M5 7.5l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span><span class="cb__pick-value" aria-hidden="true"></span><select class="cb__pick-select" data-cb-pick="${name}"></select></label>`;
    }

    // Phones: design, finish and size as compact pickers (desktop keeps its button rows).
    renderOptions() {
      const el = this.els.options;
      const type = this.type;
      if (!el || !type?.baseMap) return;
      if (!el.querySelector('[data-pick]')) el.insertAdjacentHTML('afterbegin', this.pickHtml('type', 'Design') + this.pickHtml('base', 'Finish') + this.pickHtml('size', 'Size'));
      const pick = (name) => el.querySelector(`[data-pick="${name}"]`);
      const select = (name) => el.querySelector(`[data-cb-pick="${name}"]`);
      pick('type').hidden = this.types.length < 2;
      this.syncSelect(select('type'), this.types.map((t) => ({ value: t.id, text: t.label })), this.activeType);
      pick('base').hidden = !type.bases.length;
      const oneProduct = new Set(type.bases.map((b) => b.productId)).size === 1;
      this.syncSelect(select('base'), type.bases.map((b) => {
        const name = metalLabel(b.metal) || (oneProduct ? b.optionLabel || b.name : b.name);
        return { value: b.key, text: type.flexible ? `${name} · ${b.available ? this.formatMoney(b.price) : 'sold out'}` : name, disabled: !b.available };
      }), this.build.baseKey || '');
      pick('size').hidden = !type.choices.length;
      // Closed: "7–7.5 in · 18" so the row fits 360px phones with Undo and Redo ("18 links" on 320px
      // phones); open, the menu lists "7–7.5 in · 18 links".
      const media = (q) => !!window.matchMedia?.(q).matches;
      const roomy = !media('(max-width: 749px)') && !media('(max-height: 500px)');
      const narrow = media('(max-width: 349px)');
      const closed = (c) => (!c.label ? `${c.links} links` : roomy ? `${c.label} · ${c.links} links` : narrow ? `${c.links} links` : `${c.label} · ${c.links}`);
      this.syncSelect(select('size'), type.choices.map((c) => ({ value: String(c.links), text: c.label ? `${c.label} · ${c.links} links` : `${c.links} links`, short: closed(c) })), String(this.build.length));
    }

    // Phones: charm theme and color pickers plus a search toggle, in one bar above the grid.
    renderBrowsePicks() {
      const el = this.els.browsePicks;
      if (!el || !this.charms) return;
      if (!el.firstChild) {
        el.innerHTML = this.pickHtml('category', 'Theme') + this.pickHtml('metal', 'Color') +
          `<button type="button" class="cb__icon-btn" data-cb-search-toggle aria-controls="${esc(this.els.search.id)}" aria-expanded="false"><svg aria-hidden="true" viewBox="0 0 20 20" width="20" height="20"><circle cx="8.5" cy="8.5" r="5.5" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M13 13l4.5 4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg><span class="visually-hidden">Search charms</span></button>`;
      }
      const buttons = [...this.els.categories.querySelectorAll('[data-category]')];
      el.querySelector('[data-pick="category"]').hidden = this.els.categories.hidden || !buttons.length;
      this.syncSelect(el.querySelector('[data-cb-pick="category"]'), buttons.map((b) => ({ value: b.dataset.category, text: b.textContent })), this.filters.category);
      this.syncSelect(el.querySelector('[data-cb-pick="metal"]'), ['all', 'silver', 'gold'].map((m) => ({ value: m, text: m === 'all' ? 'All' : metalLabel(m) })), this.filters.metal);
      const searching = this.hasAttribute('data-searching');
      const toggle = el.querySelector('[data-cb-search-toggle]');
      toggle.setAttribute('aria-expanded', String(searching));
      toggle.querySelector('.visually-hidden').textContent = searching ? 'Close search' : 'Search charms';
    }

    renderTypes() {
      const el = this.els.types;
      if (this.types.length < 2) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      el.innerHTML = this.types
        .map(
          (t) =>
            `<button type="button" class="cb__type" data-type="${esc(t.id)}" aria-pressed="${t.id === this.activeType}">${esc(t.label)}</button>`
        )
        .join('');
    }

    renderBases() {
      const type = this.type;
      const el = this.els.bases;
      if (!type.bases.length) {
        el.innerHTML = `<p class="cb__label">Base</p><p class="cb__muted">${type.flexible ? 'No keychain hardware is configured yet.' : 'Plain-link products must be configured before purchasing a bracelet.'}</p>`;
        return;
      }
      if (!type.flexible) {
        el.innerHTML = `<p class="cb__label">Base</p><div class="cb__base-list" role="group" aria-label="Plain link finish">${type.bases.map((b) => `<button type="button" class="cb__base" data-base="${esc(b.key)}" aria-pressed="${b.key === this.build.baseKey}"><span class="cb__swatch cb__swatch--${esc(b.metal)}" aria-hidden="true"></span>${esc(metalLabel(b.metal))}</button>`).join('')}</div>`;
        return;
      }
      // One base product in several colors reads best as just "Silver" / "Gold".
      const oneProduct = new Set(type.bases.map((b) => b.productId)).size === 1;
      el.innerHTML = `
        <p class="cb__label" id="cb-base-label-${esc(type.id)}">Base</p>
        <div class="cb__base-list" role="group" aria-labelledby="cb-base-label-${esc(type.id)}">
          ${type.bases
            .map((b) => {
              const checked = b.key === this.build.baseKey;
              const variant = b.optionLabel;
              const label = metalLabel(b.metal) || (oneProduct ? variant || b.name : variant ? `${b.name}, ${variant}` : b.name);
              const baseLinks = type.flexible ? 0 : this.baseLinks(type, b);
              const links = baseLinks ? `${baseLinks} links, ` : '';
              return `<button type="button" class="cb__base" data-base="${esc(b.key)}" aria-pressed="${checked}"
                aria-label="${esc(`${b.title}${variant ? `, ${variant}` : ''}, ${links}${b.available ? this.formatMoney(b.price) : 'sold out'}`)}" ${
                b.available ? '' : 'disabled'
              }>
                <span class="cb__base-img">${b.image ? `<img src="${esc(sizedImage(b.image, 160))}" alt="" loading="lazy" width="40" height="40">` : ''}</span>
                <span class="cb__base-text">
                  <span class="cb__base-name">${esc(label)}</span>
                  <span class="cb__base-price">${esc(links)}${b.available ? esc(this.formatMoney(b.price)) : 'Sold out'}</span>
                </span>
              </button>`;
            })
            .join('')}
        </div>`;
    }

    renderSizes() {
      const type = this.type;
      const el = this.els.sizes;
      if (!type.choices.length) {
        el.hidden = true;
        el.innerHTML = '';
        return;
      }
      el.hidden = false;
      el.innerHTML = `
        <p class="cb__label" id="cb-size-label-${esc(type.id)}">${esc(type.sizeHeading || 'Size')}</p>
        <div class="cb__size-list" role="group" aria-labelledby="cb-size-label-${esc(type.id)}">
          ${type.choices
            .map(
              (c) => `<button type="button" class="cb__size" data-size="${c.links}"
                aria-pressed="${c.links === this.build.length}" aria-label="${esc(sizeText(c))}">
                <span class="cb__size-label">${c.links} links</span>
                ${c.label ? `<span class="cb__size-links">${esc(c.label)}</span>` : ''}
              </button>`
            )
            .join('')}
        </div>
        <details class="cb__size-guide"><summary>Find your bracelet size</summary>
          <p>Measure around your wrist with a flexible tape, or use a strip of paper and a ruler.</p>
          ${type.measurementsConfirmed ? `<table><caption>Wrist size reference</caption><thead><tr><th>Links</th><th>Wrist size</th></tr></thead><tbody>${type.choices.map((c) => `<tr><td>${c.links}</td><td>${esc(c.label || '—')}</td></tr>`).join('')}</tbody></table><p>If your measurement is near the upper end of a size range, choose the next size up.</p>` : '<p>Choose a link count above. For help matching your wrist measurement to a link count, contact us before ordering. If you are near the upper end of the recommended size range, size up.</p>'}
          <p>You can remove links or add more later to adjust the fit.</p>
        </details>`;
    }

    renderPreview() {
      const type = this.type;
      this.dataset.layout = 'flat';
      const slots = this.build.slots;
      const n = slots.length;
      const plainCount = slots.filter(plainMetalOf).length;
      const filled = slots.filter(Boolean).length - plainCount;
      const previewSlots = !type.flexible ? slots : slots.length >= this.maxCharms() ? slots : slots.concat(new Array(Math.max(1, 2 - slots.length)).fill(null));
      const emptyCount = type.flexible ? 0 : n - filled - plainCount;
      const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;
      const countText = `${plural(filled, 'charm')} · ${plural(plainCount, 'plain link')}${emptyCount ? ` · ${emptyCount} empty` : ''}`;
      const issues = this.totals().issues;
      const el = this.els.preview;
      // Re-rendering replaces the scroller; keep the shopper's place in a long bracelet or keychain.
      const scrolled = el.querySelector('.cb__flat-scroll, .cb__product-scroll')?.scrollLeft || 0;
      // Phones always show the pinned design itself; the link-grid editor is a desktop view.
      const view = this.app ? 'ring' : this.view;
      el.className = `cb__preview cb__preview--${type.layout} cb__preview--${this.baseMetal()}`;

      const linkHtml = (key, i, style = '') => {
        const plain = plainMetalOf(key);
        const charm = key && !plain && this.charmMap.get(key);
        const selected = this.selected === i;
        const issue = issues.some((v) => v.index === i);
        const label = issue ? `Link ${i + 1}: unavailable selection, replace or remove` : charm ? `Link ${i + 1}: ${charm.name}` : `Link ${i + 1}: ${plain ? `plain ${plain} link` : (type.flexible ? 'empty position, drop a charm or plain link here' : 'empty, add a charm or plain link')}`;
        // Empty bracelet links show the finish's plain link faded; keychain placeholders use the bundled art.
        const face = plain ? this.plainImage(160, plain) : !key ? (type.flexible ? this.config.blankImages[this.baseMetal()] : this.plainImage(160)) : '';
        return `<div role="button" tabindex="0" class="cb__link${issue ? ' has-issue' : ''}${plain ? ` is-plain is-plain--${plain}` : ''}${charm ? ' is-filled' : ''}${selected ? ' is-selected' : ''}"
          data-slot="${i}" ${type.flexible && !key ? 'data-cb-append' : ''} aria-label="${esc(label)}" aria-pressed="${selected}" draggable="${(charm || plain) && this.nativeDrag ? 'true' : 'false'}" ${style}>
          ${charm && charm.image ? `<img src="${esc(sizedImage(charm.image, 160))}" alt="" draggable="false">` : ''}
          ${face ? `<img class="cb__plain-face" src="${esc(face)}" alt="" draggable="false">` : ''}
          ${issue ? '<span class="cb__issue-mark" aria-hidden="true">!</span>' : ''}
          <span class="cb__link-num" aria-hidden="true">${type.flexible && !key ? '+' : i + 1}</span>
        </div>`;
      };

      if (type.flexible) {
        const metal = this.baseMetal();
        const hardware = this.config.keychainHardware?.[metal];
        // Viewports retain the source pixels: clasp + end link, then end link + ring.
        const part = (name, label) => {
          const box = name === 'top' ? '0 0 306 225' : name === 'bottom' ? '420 0 280 225' : '306 0 114 225';
          return `<svg class="cb__hardware cb__hardware--${name} cb__connector" viewBox="${box}" role="img" aria-label="${label}"><image href="${esc(hardware)}" width="700" height="225" transform="translate(700 225) rotate(180)"/></svg>`;
        };
        const unit = 44;
        const editor = view === 'links';
        el.classList.add('cb__preview--studio');
        el.innerHTML = `<div class="cb__product-stage${editor ? ' cb__product-stage--editor' : ''}" style="--kc-unit:${unit.toFixed(2)}px">
          <div class="cb__stage-heading"><span>${metalLabel(metal)} keychain</span><span>${plural(filled, 'charm')}${plainCount ? ` · ${plural(plainCount, 'plain link')}` : ''}</span></div>
          ${editor ? `<div class="cb__edit-direction">Clip end <span aria-hidden="true">→</span> Ring end</div><div class="cb__link-editor">${previewSlots.map((key, i) => linkHtml(key, i)).join('')}</div>` : `
          <div class="cb__product-scroll" tabindex="0" role="region" aria-label="Keychain product preview, clasp to ring from left to right. Scroll to see all charms.">
            <div class="cb__keychain-object">
              ${part('top', 'Clasp and fixed clip-side connector')}
              <div class="cb__photo-chain">${previewSlots.map((key, i) => linkHtml(key, i)).join('')}</div>
              ${part('bottom', 'Fixed ring-side connector and split ring')}
            </div>
          </div>`}
        </div>`;
      } else if (view === 'links') {
        el.innerHTML = `<div class="cb__link-editor">${slots.map((key, i) => linkHtml(key, i)).join('')}</div><p class="cb__chain-count">${countText}. Select a link to edit.</p>`;
      } else if (type.layout === 'ring') {
        el.innerHTML = `<div class="cb__flat-heading"><span>${n} links · ${plural(filled, 'charm')}</span></div>
          <div class="cb__flat-scroll" tabindex="0" role="region" aria-label="Bracelet links, left to right. Scroll to see all links.">
            <div class="cb__flat-chain">${slots.map((key, i) => linkHtml(key, i)).join('')}</div>
          </div><p class="cb__chain-count">${countText} · ${this.touch ? 'Tap a link to change it, or press and hold to drag it.' : 'Select a link to replace it. Drag to rearrange, or drop off your design to remove.'}</p>`;
      } else {
        el.innerHTML = `
          <div class="cb__chain" style="--link:${Math.min(5.6, Math.max(3.6, 32 / n)).toFixed(2)}rem">
            <span class="cb__keyring" aria-hidden="true"></span>
            ${slots.map((key, i) => linkHtml(key, i)).join('')}
            <p class="cb__chain-count">${countText}</p>
          </div>`;
      }
      const scroller = el.querySelector('.cb__flat-scroll, .cb__product-scroll');
      if (scroller && scrolled) scroller.scrollLeft = scrolled;
      if (type.flexible) this.fitKeychain(previewSlots.length);
      el.insertAdjacentHTML('beforeend', `${view !== 'links' ? '<p class="cb__scroll-hint">Swipe to see all links →</p>' : ''}<div class="cb__discovery"><h3 id="cb-charms-${esc(this.dataset.sectionId)}" class="cb__subtitle h4">Find your little things</h3><p>The people, places, and passions that make you, you. ${this.touch ? 'Tap a charm to add it, or press and hold to drag it.' : 'Tap or drag a charm to add it.'}</p></div>`);
    }

    // Size the keychain photo to its preview: up to 44px per link, at least 34px so charms stay legible.
    // When it still overflows (phones), keep the newest charm in view after the chain grows or shrinks.
    fitKeychain(positions = this.build.slots.length + 1) {
      const scroller = this.els.preview.querySelector('.cb__product-scroll');
      const stage = this.els.preview.querySelector('.cb__product-stage');
      if (!scroller || !stage) return;
      const units = 5.1 + 4.666667 + 0.95 * positions;
      // The pinned keychain always shows whole, clasp to ring (owner request, October 9): its links scale
      // from 32px (44px on desktop) down as charms are added, as small as 12px before it would scroll,
      // inside a fixed-height area so adding charms never changes how much of the grid shows. A phone
      // on its side has little height to spare, so its keychain stays small.
      const largest = window.matchMedia?.('(max-height: 500px)').matches ? 22 : window.matchMedia?.('(min-width: 990px)').matches ? 44 : 32;
      const fit = (count) => Math.max(12, Math.min(largest, Math.floor(scroller.clientWidth / (5.1 + 4.666667 + 0.95 * count))));
      const unit = this.app ? fit(positions) : Math.max(34, Math.min(44, Math.floor(scroller.clientWidth / units)));
      stage.style.setProperty('--kc-unit', `${unit}px`);
      // The area is as tall as the largest photo this width allows (an empty keychain's two positions).
      if (this.app) stage.style.setProperty('--kc-box', `${(fit(2) * 3.75).toFixed(2)}px`);
      const count = this.build.slots.length;
      if (count !== this.fittedCount && scroller.scrollWidth > scroller.clientWidth) {
        // Newest charm at 60% of the width: the charms stay in view with a little of the ring after them.
        const target = scroller.querySelectorAll('.cb__photo-chain [data-slot]')[Math.max(0, count - 1)];
        const offset = target ? target.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft : 0;
        if (target) scroller.scrollLeft = Math.max(0, offset + target.offsetWidth / 2 - scroller.clientWidth * 0.6);
      }
      this.fittedCount = count;
    }

    renderDetail() {
      const el = this.els.detail;
      el.hidden = this.selected == null;
      // Phones swap the design toolbar (collapse, undo, redo) for the link's own actions.
      this.toggleAttribute('data-selected', this.selected != null);
      const i = this.selected;
      if (i == null) {
        el.innerHTML = '';
        return;
      }
      const key = this.build.slots[i];
      const plain = plainMetalOf(key);
      const charm = key && !plain && this.charmMap.get(key);
      const n = this.build.slots.length;
      // One button per configured finish, skipping the finish this link already has.
      const plainButtons = (verb) => PLAIN_METALS.filter((m) => m !== plain && this.plainVariant(m))
        .map((m) => `<button type="button" class="cb__text-btn" data-action="plain" data-metal="${m}">${verb} plain ${m}</button>`).join('');
      const moves = `${i > 0 ? `<button type="button" class="cb__text-btn" data-action="move" data-to="${i - 1}">Move to link ${i}</button>` : ''}
          ${i < n - 1 ? `<button type="button" class="cb__text-btn" data-action="move" data-to="${i + 1}">Move to link ${i + 2}</button>` : ''}`;
      const issue = key && this.totals().issues.some((v) => v.index === i);
      if (this.app) {
        // Phones: one toolbar row under the pinned design, the same buttons as the desktop panel.
        const arrow = (to, dir) => `<button type="button" class="cb__act cb__act--icon" data-action="move" data-to="${to}" aria-label="Move to link ${to + 1}"${to < 0 || to >= n ? ' disabled' : ''}><svg aria-hidden="true" viewBox="0 0 20 20" width="18" height="18"><path d="${dir < 0 ? 'M12.5 5l-5 5 5 5' : 'M7.5 5l5 5-5 5'}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>`;
        const plainActs = PLAIN_METALS.filter((m) => m !== plain && this.plainVariant(m))
          .map((m) => `<button type="button" class="cb__act" data-action="plain" data-metal="${m}" aria-label="Plain ${m} link here"><span class="cb__swatch cb__swatch--${m}" aria-hidden="true"></span>${metalLabel(m)}</button>`).join('');
        el.innerHTML = `<div class="cb__actions" role="group" aria-label="${esc(this.slotSummary(i))}">
          ${key && !issue ? arrow(i - 1, -1) + arrow(i + 1, 1) : ''}
          ${key ? `<button type="button" class="cb__act cb__act--icon" data-action="remove" aria-label="Remove ${plain ? 'plain link' : 'charm'}"><svg aria-hidden="true" viewBox="0 0 20 20" width="18" height="18"><path d="M4 6h12M8 6V4.5h4V6M6 6l.8 10h6.4L14 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></button>` : ''}
          ${plainActs}
          <button type="button" class="cb__act cb__act--icon cb__act--done" data-action="done" aria-label="Done"><svg aria-hidden="true" viewBox="0 0 20 20" width="18" height="18"><path d="M5.5 5.5l9 9M14.5 5.5l-9 9" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>
        </div>`;
        return;
      }
      if (issue) {
        el.innerHTML = `<p role="status">Link ${i + 1}: this selection is no longer available in the required quantity. Choose a replacement or remove it.</p>${plainButtons('Replace with')}<button type="button" class="cb__text-btn" data-action="remove">Remove charm</button>`;
        return;
      }
      if (plain) {
        const item = this.plainVariant(plain);
        el.innerHTML = `
          <div class="cb__detail-row">
            <p><strong>Plain ${plain} link ${i + 1}${item ? `, ${esc(this.formatMoney(item.price))}` : ''}.</strong> Choose a charm to place it here.</p>
          </div>
          <div class="cb__detail-actions">
            ${moves}
            ${plainButtons('Change to')}
            <button type="button" class="cb__text-btn" data-action="remove">Remove plain link</button>
            <button type="button" class="cb__text-btn" data-action="done">Cancel</button>
          </div>`;
        return;
      }
      if (!charm) {
        el.innerHTML = `
          <div class="cb__detail-row">
            <p><strong>Empty position ${i + 1}.</strong> Choose a charm to place it here.</p>
            ${plainButtons('Make it')}
            <button type="button" class="cb__text-btn" data-action="done">Cancel</button>
          </div>`;
        return;
      }
      const metal = charm.metal && charm.metal !== 'both' ? metalLabel(charm.metal) : '';
      el.innerHTML = `
        <div class="cb__detail-card">
          ${charm.image ? `<img src="${esc(sizedImage(charm.image, 240))}" alt="" width="64" height="64">` : ''}
          <div class="cb__detail-text">
            <p class="cb__detail-name">${esc(charm.name)}</p>
            <p class="cb__muted">Link ${i + 1}${metal ? `, ${metal}` : ''}, ${esc(this.formatMoney(charm.price))}</p>
          </div>
        </div>
        <div class="cb__detail-actions">
          ${moves}
          ${plainButtons('Replace with')}
          <button type="button" class="cb__text-btn" data-action="remove">Remove charm</button>
          <button type="button" class="cb__text-btn" data-action="done">Done</button>
        </div>`;
    }

    totals() {
      const type = this.type;
      const base = type.baseMap.get(this.build.baseKey) || null;
      const positions = new Map();
      const issues = [];
      const charms = [];
      const plainLinks = { silver: [], gold: [] };
      this.build.slots.forEach((key, index) => {
        if (!key) return;
        const metal = plainMetalOf(key);
        if (metal) { plainLinks[metal].push(index + 1); return; }
        const charm = this.charmMap.get(key);
        if (!positions.has(key)) positions.set(key, []);
        positions.get(key).push(index + 1);
        if (!charm?.available || positions.get(key).length > this.remaining(charm)) issues.push({ index, key });
        if (charm) charms.push(charm);
      });
      // Only placed plain links are bought, one line per finish; empty bracelet positions block the cart.
      const plain = PLAIN_METALS.filter((metal) => plainLinks[metal].length)
        .map((metal) => ({ metal, item: this.plainVariant(metal), links: plainLinks[metal], quantity: plainLinks[metal].length }));
      const blanks = plain.reduce((n, g) => n + g.quantity, 0);
      const empty = type.flexible ? 0 : this.build.slots.filter((v) => !v).length;
      const groups = [...positions].map(([key, links]) => ({ item: this.charmMap.get(key), links, quantity: links.length })).filter((g) => g.item);
      const charmTotal = charms.reduce((sum, c) => sum + c.price, 0);
      const plainTotal = plain.reduce((sum, g) => sum + (g.item ? g.item.price * g.quantity : 0), 0);
      // Keychains buy one hardware base; a bracelet's base is only its default plain-link finish.
      const baseQuantity = type.flexible ? 1 : blanks;
      const total = (type.flexible && base ? base.price : 0) + plainTotal + charmTotal;
      const short = plain.find((g) => !g.item || !g.item.available || g.quantity > this.remaining(g.item));
      let hint = '';
      if (!base) hint = 'Choose a configured finish to continue.';
      else if (issues.length) hint = 'Review the marked links. Replace or remove unavailable selections.';
      else if (type.flexible && !charms.length) hint = 'Add at least one charm to your keychain.';
      else if (type.flexible && this.build.slots.length > this.maxCharms()) hint = `Keychains hold up to ${this.maxCharms()} links. Remove ${this.build.slots.length - this.maxCharms()} to continue.`;
      else if (empty) hint = `Fill ${empty} empty ${empty === 1 ? 'link' : 'links'} with a charm or plain link to continue.`;
      else if (type.flexible && (!base.available || 1 > this.remaining(base))) hint = 'This keychain base is unavailable.';
      else if (short) hint = `There are not enough plain ${short.metal} links. Replace some with charms or plain ${otherMetal(short.metal)} links.`;
      return { base, charms, groups, plain, blanks, empty, baseQuantity, charmTotal, plainTotal, total, issues, hint };
    }

    renderSummary() {
      if (!this.type?.baseMap) return;
      const { base, groups, plain, charms, blanks, empty, total, issues, hint } = this.totals();
      const type = this.type;
      const row = (label, value, cls = '') => `<div class="cb__summary-row ${cls}"><dt>${esc(label)}</dt><dd>${esc(value)}</dd></div>`;
      this.els.summary.innerHTML =
        (type.flexible ? row(`${metalLabel(this.baseMetal())} keychain base`, base ? this.formatMoney(base.price) : 'Choose finish') : '') +
        plain.map(({metal, item, quantity}) => row(`${quantity} plain ${metal} ${quantity === 1 ? 'link' : 'links'}`, item ? this.formatMoney(item.price * quantity) : 'Unavailable')).join('') +
        groups.map(({item, quantity}) => row(`${quantity > 1 ? `${quantity} × ` : ''}${item.name}`, this.formatMoney(item.price * quantity))).join('') +
        row(issues.length ? 'Review required' : empty ? 'Total so far' : 'Total', issues.length ? 'Resolve marked links' : this.formatMoney(total), 'cb__summary-total');
      const blankNotice = this.querySelector('[data-cb-blank-notice]');
      blankNotice.hidden = type.flexible || !empty;
      blankNotice.innerHTML = empty ? `${empty} ${empty === 1 ? 'link is' : 'links are'} still empty. Fill each with a charm or a plain link. <button type="button" class="cb__text-btn" data-cb-fill-plain>Fill ${empty === 1 ? 'it' : `all ${empty}`} with plain ${esc(this.baseMetal())} links</button>` : '';
      const mobileNotice = this.querySelector('[data-cb-bar-plain]');
      mobileNotice.hidden = type.flexible || !empty;
      mobileNotice.textContent = `${empty} empty ${empty === 1 ? 'link' : 'links'} to fill`;
      const ready = !hint && !this.busy && !this.leaving;
      // aria-disabled keeps the buttons focusable so keyboard and screen-reader users hear why.
      for (const button of [this.els.add, this.els.barAdd]) {
        button.disabled = false;
        button.setAttribute('aria-disabled', String(!ready));
      }
      this.els.add.textContent = this.busy ? 'Checking your design…' : `Add ${type.label.toLowerCase()} to cart`;
      this.els.barAdd.textContent = this.busy ? 'Checking…' : 'Add to cart';
      this.els.hint.textContent = hint;
      this.els.hint.hidden = !hint;
      this.els.barTotal.textContent = issues.length ? 'Review marked links' : this.formatMoney(total);
      // Kept short for 320px phones; the empty count has its own line below. A change message keeps
      // the line until its timer restores this summary.
      if (!this.els.bar.classList.contains('has-message')) this.els.barStatus.textContent = this.barStatus(charms, blanks);
    }

    barStatus(charms, blanks) {
      // Phones hide the separate empty-links line, so the count joins the status.
      if (this.app && !this.type.flexible) {
        const empty = this.build.slots.filter((v) => !v).length;
        return [countLabel(charms.length, 'charm'), blanks ? `${blanks} plain` : '', empty ? `${empty} empty` : ''].filter(Boolean).join(' · ');
      }
      if (!this.type.flexible) return `${countLabel(charms.length, 'charm')} · ${blanks} plain`;
      const max = this.maxCharms();
      return blanks ? `${countLabel(charms.length, 'charm')} · ${blanks} plain · ${this.build.slots.length} of ${max}` : `${charms.length} of ${max} charms`;
    }

    /* ----- phone bar ----- */

    watchPanel() {
      const bar = this.els.bar;
      this.positionPanel = () => {
        const offset = Math.min(24, window.innerHeight - this.els.panel.getBoundingClientRect().height - 24);
        this.els.panel.style.setProperty('--cb-sticky-top', `${offset}px`);
        // Phones pin the design panel, then the theme/color bar right under it, then the grid scrolls.
        // Rounded down so the theme bar tucks under the panel's edge rather than leaving a sliver.
        this.style.setProperty('--cb-panel-h', `${Math.floor(this.els.panel.getBoundingClientRect().height)}px`);
        this.style.setProperty('--cb-bar-h', `${Math.round(bar.getBoundingClientRect().height)}px`);
        // Best effort: Shopify's theme-preview bar is fixed at the bottom; keep the cart bar above it.
        const preview = document.getElementById('preview-bar-iframe');
        const cover = preview ? Math.max(0, window.innerHeight - preview.getBoundingClientRect().top) : 0;
        this.style.setProperty('--cb-bottom', `${Math.min(cover, 120)}px`);
      };
      if ('ResizeObserver' in window) { this.panelObserver = new ResizeObserver(this.positionPanel); this.panelObserver.observe(this.els.panel); this.panelObserver.observe(bar); }
      window.addEventListener('resize', this.positionPanel);
      this.refit = () => {
        if (this.type?.flexible && (this.app || this.view !== 'links')) this.fitKeychain();
        // Turning a phone changes how much room the Size picker has.
        this.renderOptions();
      };
      window.addEventListener('resize', this.refit);
      this.positionPanel();
      // Below 990px the bar keeps the total and cart button in reach. Tablets hide it while the order
      // summary's own button is on screen; phones (whose summary is a sheet) show it while the builder is.
      this.barSeen = { add: false, layout: true };
      this.updateBar = () => {
        const hidden = this.app ? !this.barSeen.layout : this.barSeen.add;
        if (bar.hidden !== hidden) { bar.hidden = hidden; this.positionPanel(); }
        if (hidden) this.setOrderOpen(false);
      };
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => { this.barSeen[entry.target === this.els.add ? 'add' : 'layout'] = entry.isIntersecting; });
          this.updateBar();
        },
        { threshold: 0 }
      );
      this.observer.observe(this.els.add);
      this.observer.observe(this.els.layout);
    }

    /* ----- cart ----- */

    // "1 Heart | 2 Plain | 3 Block Letters · A (Gold) | 4 Plain (Gold)": the whole design on every line,
    // for packing slips. A finish is named only when it differs from the design's base (bracelet finish
    // or keychain hardware). Names may contain " · ", so positions are separated by " | ".
    layout() {
      const baseMetal = this.baseMetal();
      const finish = (metal) => (PLAIN_METALS.includes(metal) && metal !== baseMetal ? ` (${metalLabel(metal)})` : '');
      return this.build.slots.map((key, i) => {
        const plain = plainMetalOf(key);
        if (plain) return `${i + 1} Plain${finish(plain)}`;
        const charm = key && this.charmMap.get(key);
        // Empty or unavailable positions never reach the cart; they still identify a pending design.
        if (!charm) return `${i + 1} ${key ? 'Unavailable' : 'Empty'}`;
        return `${i + 1} ${charm.name.replace(/\|/g, '/')}${finish(charm.metal)}`;
      }).join(' | ');
    }

    cartItems(code) {
      const {base, groups, plain, charms, blanks} = this.totals();
      const type = this.type;
      // Shoppers see the design, build number, size and a one-line assembly note. Underscore
      // properties are hidden at checkout and stay on the order in Shopify Admin and on the packing slip.
      const keychainSize = blanks ? `${countLabel(charms.length, 'charm')}, ${countLabel(blanks, 'plain link')}` : countLabel(charms.length, 'charm');
      const common = {
        _cb_build: code, _cb_type: type.id, _cb_layout: this.layout(), 'Design': `${metalLabel(this.baseMetal())} ${type.label.toLowerCase()}`,
        [`${type.label} build`]: code,
        'Size': type.flexible ? keychainSize : `${this.build.slots.length} links`,
        'Assembly': 'Built for you by Synergy',
        '_Assembly': type.flexible
          ? `Keep both end connectors. Replace the two plain middle links with the selected charms${blanks ? ' and plain links, in the listed order' : ''}.`
          : 'Assemble all listed components into one bracelet. Positions run left to right from link 1 in the flat preview.',
      };
      if (type.flexible) {
        common['_Charm order'] = 'Numbered from clip to ring; fixed connectors are not numbered.';
        common['_Orientation'] = 'Clasp above ring; tops of charms face right.';
        common['_Charm count'] = String(charms.length);
      }
      common['_Plain links'] = String(blanks);
      const items = [];
      if (type.flexible && base) items.push({id: base.id, quantity: 1, properties: {...common, '_Component': 'Hardware'}});
      plain.forEach(({item, quantity, links}) => item && items.push({id: item.id, quantity, properties: {...common, '_Component': 'Plain links', '_Links': links.join(', ')}}));
      groups.forEach(({item, quantity, links}) => items.push({id: item.id, quantity, properties: {...common, '_Component': 'Charm', [links.length > 1 ? '_Links' : '_Link']: links.join(', ')}}));
      return items;
    }

    savePending(value) {
      this.pending = value;
      try { if (value) sessionStorage.setItem(this.pendingKey, JSON.stringify(value)); else sessionStorage.removeItem(this.pendingKey); } catch { /* In-memory reconciliation remains available. */ }
    }

    // Identifies the design a pending cart request was made for.
    signature() {
      return `${this.type?.id}|${this.build?.baseKey}|${this.layout()}`;
    }

    // Checks whether an earlier, unconfirmed add reached the cart. Returns true only when it was
    // the current design. After a network failure the request may still land, so it stays pending.
    async reconcile({ clearIfMissing = true } = {}) {
      if (!this.pending) return false;
      const cart = await this.readCart();
      const lines = cart.items.filter((i) => i.properties?._cb_build === this.pending.code);
      if (!lines.length) { if (clearIfMissing) this.savePending(null); return false; }
      const complete = lines.length === this.pending.items.length && this.pending.items.every((expected) => lines.some((line) => String(line.variant_id || line.id) === String(expected.id) && line.quantity === expected.quantity));
      if (!complete) throw Object.assign(new Error('Some design components are already in your cart. Remove that design from the cart before trying again.'), { partial: true });
      const current = this.pending.signature === this.signature();
      this.savePending(null);
      if (!current) {
        this.announce('Your earlier design is already in your cart.', true);
        return false;
      }
      this.cartComplete();
      return true;
    }

    // Shopify customer event for a custom pixel. Absent outside a Shopify storefront.
    trackAdded() {
      const publish = window.Shopify?.analytics?.publish;
      if (typeof publish !== 'function') return;
      const { charms, total } = this.totals();
      try {
        publish.call(window.Shopify.analytics, 'charm_studio_design_added', {
          design_type: this.type.id,
          metal: this.baseMetal(),
          size: this.build.slots.length,
          charm_count: charms.length,
          value: total / 100,
          currency: this.config.currency || 'USD',
        });
      } catch { /* Measurement must never affect the cart. */ }
    }

    cartComplete(data, body = {}) {
      this.announce(`${this.type.label} added to cart.`);
      this.trackAdded();
      if (!this.dispatchEvent(new CustomEvent('cb:cart-added', { bubbles: true, cancelable: true }))) return;
      const drawer = document.querySelector('cart-drawer');
      const ready = body.sections?.every((id) => typeof data?.sections?.[id] === 'string');
      if (drawer && typeof drawer.renderContents === 'function' && ready) {
        drawer.classList.remove('is-empty');
        try { drawer.renderContents(data); } catch { this.leaving = true; window.location.href = `${this.root}cart`; }
      } else { this.leaving = true; window.location.href = `${this.root}cart`; }
    }

    async addToCart() {
      if (this.busy || this.leaving) return;
      // The buttons stay focusable while blocked, so explain instead of submitting.
      const blocked = this.type?.baseMap && this.totals().hint;
      if (blocked) {
        this.announce(blocked, true);
        // Phones: the sheet shows what is missing and offers "Fill all with plain links".
        this.setOrderOpen(true);
        return;
      }
      this.busy = true;
      this.renderSummary();
      this.showError('');
      let submitted = false;
      let responded = false;
      try {
        if (await this.reconcile()) return;
        const previous = this.totals().total;
        await this.readCatalog();
        await this.readCart();
        this.buildGrid(); this.renderCategories(); this.applyFilters(); this.renderAll();
        const current = this.totals();
        if (current.hint) throw new Error(current.hint);
        if (previous !== current.total) throw new Error('Prices changed. Review the updated total, then add your design again.');
        const code = `#${crypto.randomUUID().slice(0, 8).toUpperCase()}`;
        const items = this.cartItems(code);
        const body = {items};
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          body.sections = drawer.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }
        this.savePending({code, items, signature: this.signature()});
        submitted = true;
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body),
        });
        responded = true;
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || 'This design could not be added.');
        this.savePending(null);
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, {source:'charm-builder', cartData:data}); } catch { /* Do not retry a successful add. */ }
        }
        this.cartComplete(data, body);
      } catch (error) {
        if (submitted && this.pending) {
          try { if (await this.reconcile({ clearIfMissing: responded })) return; } catch (problem) {
            this.showError(problem?.partial ? problem.message : 'We could not confirm the cart result. Your design is saved. Open your cart to review it; retrying will check the cart before submitting again.');
            return;
          }
        }
        if (submitted) {
          try { await this.readCatalog(); this.buildGrid(); this.renderCategories(); this.applyFilters(); this.renderAll(); } catch { /* Keep the original actionable cart error. */ }
        }
        this.showError(error.message);
      } finally {
        this.busy = false;
        this.renderSummary();
      }
    }
  }

  customElements.define('charm-builder', CharmBuilder);
})();
